import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Select, MenuItem, Chip, Checkbox, FormControlLabel, Button, FormControl, ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { useNavigate } from 'react-router-dom';
import useRscPricing, { commercialPricePerKwp } from '../../hooks/useRscPricing';
import useCscBillProposal, { calcCommercialLvBill, estimateMonthlySaving, estimateMonthlySavingSELCo, inverterFromMd, inverterFromKwp, RATIO_CAP_NO_BATT } from '../../hooks/useCscBillProposal';
import { buildCscVarMap, generateCscProposalPdf, todayDMY, plus30DMY, MY_STATE_GHI, cscSubstitute, postProcessCscProposal } from './cscProposalEngine';
import useCscProposalMath from '../../hooks/useCscProposalMath';
import { buildCscWhatsAppMsg } from './cscWhatsApp';
import CscEnergyChart from './cscEnergyChart';
import { generateCommercialQuotation2, generateCommercialAtap, generateCommercialDeclaration, generateCommercialInsurance2, generateCommercialAll, getSalesTitle } from '../../api/formGeneratorApi';
import { getAddons } from '../../api/resourceApi';
import { getAllAgentGroups, getAgentsByGroupId, getAgentDetailsByAgentId } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import { createSales } from '../../api/salesApi';

// Parse a catalog addon's price into { base, perUnit, total } for a given qty.
// Copied VERBATIM from residentialSolarCalculatorV2.jsx — the "+" base+rate
// syntax ("700+170" = RM 700 setup + RM 170 per unit) is honored FIRST,
// regardless of unitType, so a stale/missing unitType can't concatenate the
// digits into "700170". (Bug fix 2026-06-01.)
function computeAddonAmount(addon, qty) {
    if (!addon) return { base: 0, perUnit: 0, total: 0 };
    const raw = String(addon.price ?? '');
    const cleaned = raw.replace(/[^\d.+]/g, '');
    if (cleaned.includes('+')) {
        const [b, p] = cleaned.split('+');
        const base = parseFloat(b) || 0;
        const perUnit = parseFloat(p) || 0;
        return { base, perUnit, total: base + perUnit * (Number(qty) || 0) };
    }
    const qtyBearing = addon.unitType === 'PerPanel' || addon.unitType === 'PerItem' || addon.unitType === 'PerYear';
    if (qtyBearing) {
        const perUnit = parseFloat(cleaned) || 0;
        return { base: 0, perUnit, total: perUnit * (Number(qty) || 0) };
    }
    // 'None' or anything else — treat as a literal one-shot price.
    const num = parseFloat(raw.replace(/[^\d.]/g, '')) || 0;
    return { base: 0, perUnit: num, total: num };
}

const PP = "'Poppins', sans-serif";
// Single-language label helper — show ONLY the active language (no bilingual
// "中文 / English" mash-up). lang: 'en' | 'zh' | 'ms'. Falls back to EN.
const tr = (lang, en, zh, ms) => (lang === 'zh' ? zh : (lang === 'ms' && ms ? ms : en));
// sessionStorage key for the CSC Combined tab draft — keeps filled data across
// page navigation (cleared on tab close or via the Clear Data button).
const CSC_DRAFT_KEY = 'csc_draft_v1';
// Absolute URL for a stored (relative) profile photo, so it loads in the proposal.
const PHOTO_API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api$/, '');
const absPhoto = (u) => (u ? `${PHOTO_API_BASE}${u}` : '');
const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF', bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', border: '#E2E8F0', success: '#10B981', warning: '#F59E0B',
};

const TARIFFS = [
    { rate: 0.515, label: 'Low Voltage (LV)' },
    { rate: 0.303, label: 'Medium Voltage (MV)' },
    { rate: 0.437, label: 'High Voltage (HV)' },
    { rate: 0.451, label: 'Agriculture' },
];

const fmtRM = (n) => 'RM ' + Math.round(Number(n) || 0).toLocaleString('en-MY');

// Battery cost folded into the system total of generated docs.
//   <100 kWh   → ceil(kWh / 16) × RM 7,400 per 16-kWh unit
//   100–1000   → kWh × RM 1,200
//   >1000      → kWh × RM 1,100
const batteryCostFor = (kwh) => {
    const b = Number(kwh) || 0;
    if (b <= 0) return 0;
    if (b < 100) return Math.ceil(b / 16) * 7400;
    return b * (b <= 1000 ? 1200 : 1100);
};

// Commercial O&M (maintenance) annual fee by kWp tier — Basic vs Premium package
// (official ATAP Solar maintenance table). Basic is Not Available above 100 kWp.
const MAINT_TIERS = [
    { max: 6.82,  basic: 600,  premium: 1800 },
    { max: 10.54, basic: 800,  premium: 2300 },
    { max: 20,    basic: 800,  premium: 2600 },
    { max: 40,    basic: 1700, premium: 3500 },
    { max: 60,    basic: 2300, premium: 4500 },
    { max: 80,    basic: 2600, premium: 4500 },
    { max: 100,   basic: 2800, premium: 4500 },
    { max: 300,   basic: null, premium: 5000 },
    { max: 600,   basic: null, premium: 7500 },
    { max: 1000,  basic: null, premium: 11000 },
];
// pkg: 'basic' | 'premium'. Falls back to premium where basic is N/A (>100 kWp).
const omForKwp = (kwp, pkg = 'premium') => {
    const k = Number(kwp) || 0;
    for (const t of MAINT_TIERS) if (k <= t.max) { const v = pkg === 'basic' ? t.basic : t.premium; return v != null ? v : t.premium; }
    return 11000;
};
// Whether the Basic package is offered for this kWp (N/A above 100 kWp).
const basicOmAvailable = (kwp) => { const k = Number(kwp) || 0; for (const t of MAINT_TIERS) if (k <= t.max) return t.basic != null; return false; };

const Label = ({ children }) => (
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
        {children}
    </Typography>
);

// Section header — 4px accent bar + bold title + thin divider line (mirrors RSC AI BESS 360)
const SectionHeader = ({ title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, mb: 1 }}>
        <Box sx={{ width: 4, height: 24, bgcolor: '#1A8CFF', borderRadius: 1 }} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, letterSpacing: '0.02em' }}>{title}</Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: '#E2E8F0' }} />
    </Box>
);

// Spec row restyled for on-gradient (white text) hero cards.
const Row = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', fontFamily: PP }}>{value}</Typography>
    </Box>
);

// One line item inside a white 原电费/新电费 bill sub-card (dark text on white).
const BillLine = ({ label, value, bold, credit }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25, ...(bold ? { borderTop: '1px solid #E2E8F0', mt: 0.4, pt: 0.5 } : {}) }}>
        <Typography sx={{ fontSize: '0.62rem', color: bold ? COLORS.text : COLORS.sub, fontWeight: bold ? 700 : 500, fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: bold ? '0.7rem' : '0.62rem', fontWeight: bold ? 800 : 600, color: credit ? COLORS.success : COLORS.text, fontFamily: "'JetBrains Mono',monospace" }}>{value}</Typography>
    </Box>
);

// A white bill breakdown box (原电费 / 新电费) — mirrors RSC AI BESS 360.
const BillBox = ({ title, sub, b, exportCredit, totalOverride }) => (
    <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, p: 1, flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: COLORS.dark, fontFamily: PP, mb: 0.4 }}>{title}</Typography>
        <BillLine label="Energy" value={b.energy.toFixed(2)} />
        <BillLine label="Capacity" value={b.capacity.toFixed(2)} />
        <BillLine label="Network" value={b.network.toFixed(2)} />
        <BillLine label="Retail" value={b.retail.toFixed(2)} />
        {b.afa ? <BillLine label="AFA" value={`${b.afa < 0 ? '−' : ''}${Math.abs(b.afa).toFixed(2)}`} credit={b.afa < 0} /> : null}
        <BillLine label="KWTBB" value={b.kwtbb.toFixed(2)} />
        {exportCredit > 0 && (
            <BillLine label="Export Cr." value={`−${exportCredit.toFixed(2)}`} credit />
        )}
        <BillLine label="Total" value={fmtRM(totalOverride != null ? totalOverride : b.total)} bold />
        {sub && <Typography sx={{ fontSize: '0.54rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>{sub}</Typography>}
    </Box>
);

// Compact translucent pill for the card's system-spec strip.
const SpecChip = ({ children }) => (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.14)', borderRadius: 1, px: 0.9, py: 0.35, display: 'inline-flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: '#fff', fontFamily: PP, whiteSpace: 'nowrap' }}>{children}</Typography>
    </Box>
);

// On-gradient label for in-card inputs / sections.
const CardLabel = ({ children, sx }) => (
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, mb: 0.5, ...sx }}>{children}</Typography>
);

// One line in the on-gradient 价格明细 price breakdown.
const PriceRow = ({ label, value, bold, discount }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3, ...(bold ? { borderTop: '1px solid rgba(255,255,255,0.2)', mt: 0.4, pt: 0.5 } : {}) }}>
        <Typography sx={{ fontSize: bold ? '0.74rem' : '0.68rem', fontWeight: bold ? 800 : 500, color: bold ? '#fff' : 'rgba(255,255,255,0.75)', fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: bold ? '0.86rem' : '0.7rem', fontWeight: bold ? 800 : 700, color: discount ? '#86EFAC' : '#fff', fontFamily: "'JetBrains Mono',monospace" }}>{value}</Typography>
    </Box>
);

// One of the ROI / CASH / 36M CC tiles.
const Tile = ({ label, value }) => (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1.5, p: 0.8, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>{value}</Typography>
    </Box>
);

// Dark-on-gradient input styling so digits stay readable on the card gradient
// (ported from RSC's add-on rows).
const ADDON_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px', bgcolor: 'rgba(15,23,42,0.55)', height: 30,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
    },
    // Self-selector — a bare <Select sx={ADDON_INPUT_SX}> has the OutlinedInput AS
    // its root, so the descendant rule above misses it (green gradient shows
    // through). Match the root directly so Selects get the same dark bar.
    '&.MuiOutlinedInput-root': {
        borderRadius: '6px', bgcolor: 'rgba(15,23,42,0.55)', height: 30,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#60a5fa' },
    },
    '& .MuiOutlinedInput-input': { padding: '4px 6px', color: '#fff', fontFamily: PP, fontSize: '0.74rem', fontWeight: 600 },
    '& .MuiSelect-select': { color: '#fff', py: '4px', pl: '8px' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.75)' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { '-webkit-appearance': 'none', margin: 0 },
    '& input[type=number]': { '-moz-appearance': 'textfield' },
};

// In-card add-on package (dark-on-gradient). Ported from RSC's "配件包" block:
// pick from the shared catalog, set qty, edit Base + per-unit Rate separately,
// per-line green total, remove rows. CSC-simplified: no phase filter, no synth
// backup-box / lock. stopPropagation so editing doesn't toggle card selection.
function CardAddons({ rows, catalog, onAdd, onUpdate, onRemove, lang }) {
    rows = rows || [];
    const grid = '1fr 50px 1fr 22px';
    const total = rows.reduce((s, a) => {
        const qty = (a.unitType === 'PerPanel' || a.unitType === 'PerItem' || a.unitType === 'PerYear')
            ? (a._qty != null && a._qty !== '' && Number.isFinite(Number(a._qty)) ? Math.max(0, Number(a._qty)) : 1) : 1;
        return s + computeAddonAmount(a, qty).total;
    }, 0);
    return (
        <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1.25 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5, fontFamily: PP }}>
                {tr(lang, 'Add-ons', '配件包')}
            </Typography>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 1 }}>
                {rows.length === 0 && (
                    <Typography sx={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.45)', fontFamily: PP, fontStyle: 'italic', textAlign: 'center', py: 0.5 }}>
                        {tr(lang, 'No add-ons yet — pick from catalog below', '暂无配件 — 从下方目录选择')}
                    </Typography>
                )}
                {rows.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: grid, gap: 0.5, alignItems: 'center', pb: 0.4, borderBottom: '1px dashed rgba(255,255,255,0.18)' }}>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP }}>{tr(lang, 'Item', '项目')}</Typography>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, textAlign: 'center' }}>{tr(lang, 'Qty', '数量')}</Typography>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, textAlign: 'center' }}>{tr(lang, 'Price', '价格')}</Typography>
                        <span />
                    </Box>
                )}
                {rows.map((r) => {
                    const qtyBearing = r.unitType === 'PerPanel' || r.unitType === 'PerItem' || r.unitType === 'PerYear';
                    const qtyShown = r._qty != null ? r._qty : 1;
                    const line = computeAddonAmount(r, Number(qtyShown) || 0);
                    const unitTag = r.unitType === 'PerPanel' ? '/panel' : r.unitType === 'PerYear' ? '/year' : r.unitType === 'PerItem' ? '/item' : '';
                    // Parse price into Base / Rate parts so each gets its own field.
                    const cleaned = String(r.price ?? '').replace(/[^\d.+]/g, '');
                    let baseStr = '', rateStr = '';
                    if (cleaned.includes('+')) { const p = cleaned.split('+'); baseStr = p[0] || ''; rateStr = p[1] || ''; }
                    else { rateStr = cleaned; }
                    const baseNum = parseFloat(baseStr) || 0;
                    const rateNum = parseFloat(rateStr) || 0;
                    const showBase = baseNum > 0;
                    const showRate = rateNum > 0 || baseNum === 0;
                    const setPriceParts = (nb, nr) => {
                        const b = (nb || '').toString().replace(/[^\d.]/g, '');
                        const rt = (nr || '').toString().replace(/[^\d.]/g, '');
                        const combined = (parseFloat(b) > 0) ? (rt === '' ? b : `${b}+${rt}`) : rt;
                        onUpdate(r.rowId, { price: combined });
                    };
                    return (
                        <Box key={r.rowId || r.addonId} sx={{ display: 'grid', gridTemplateColumns: grid, gap: 0.5, alignItems: 'center', py: 0.5, borderBottom: '1px dashed rgba(255,255,255,0.1)' }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', fontFamily: PP, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.itemName}>{r.itemName}</Typography>
                                    {unitTag && <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)', fontFamily: PP, fontWeight: 600, flexShrink: 0 }}>{unitTag}</Typography>}
                                </Box>
                                {line.total > 0 && (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#86EFAC', fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, mt: 0.1 }}>
                                        {(line.base > 0 && line.perUnit > 0)
                                            ? `RM ${line.base.toLocaleString()} + ${qtyShown}× RM ${line.perUnit.toLocaleString()} = RM ${line.total.toLocaleString()}`
                                            : `= RM ${line.total.toLocaleString()}`}
                                    </Typography>
                                )}
                            </Box>
                            <TextField size="small" type="number" value={r._qty ?? 1} disabled={!qtyBearing}
                                onChange={(e) => onUpdate(r.rowId, { _qty: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                                inputProps={{ step: 1, min: 0 }}
                                sx={{ ...ADDON_INPUT_SX, '& .MuiOutlinedInput-input': { ...ADDON_INPUT_SX['& .MuiOutlinedInput-input'], textAlign: 'center' }, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,0.6)' } }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                {showBase && (
                                    <TextField size="small" type="number" value={baseStr} placeholder="0"
                                        onChange={(e) => setPriceParts(e.target.value, rateStr)} inputProps={{ step: 1, min: 0 }}
                                        sx={{ ...ADDON_INPUT_SX, '& .MuiOutlinedInput-input': { ...ADDON_INPUT_SX['& .MuiOutlinedInput-input'], textAlign: 'right', fontSize: '0.7rem' } }}
                                        InputProps={showRate ? { startAdornment: <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.55)', fontFamily: PP, fontWeight: 600, mr: 0.4 }}>Base</Typography> } : undefined} />
                                )}
                                {showRate && (
                                    <TextField size="small" type="number" value={rateStr} placeholder="0"
                                        onChange={(e) => setPriceParts(baseStr, e.target.value)} inputProps={{ step: 1, min: 0 }}
                                        sx={{ ...ADDON_INPUT_SX, '& .MuiOutlinedInput-input': { ...ADDON_INPUT_SX['& .MuiOutlinedInput-input'], textAlign: 'right', fontSize: '0.7rem' } }} />
                                )}
                            </Box>
                            <Button size="small" onClick={() => onRemove(r.rowId)}
                                sx={{ minWidth: 0, width: 22, height: 22, p: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#FCA5A5', bgcolor: 'rgba(252,165,165,0.1)' } }}>×</Button>
                        </Box>
                    );
                })}
                {total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.6, mt: 0.4 }}>
                        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', fontFamily: PP }}>Add-ons subtotal</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>{fmtRM(total)}</Typography>
                    </Box>
                )}
                <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                    <Select size="small" fullWidth displayEmpty value=""
                        onChange={(e) => { if (e.target.value) onAdd(Number(e.target.value)); }}
                        sx={ADDON_INPUT_SX}>
                        <MenuItem value="" disabled sx={{ fontSize: '0.72rem', fontFamily: PP, fontStyle: 'italic' }}>+ {tr(lang, 'Add from catalog…', '从目录添加配件…')}</MenuItem>
                        {(() => {
                            const groups = {};
                            (catalog || []).forEach((a) => { const c = a.category || 'General'; (groups[c] = groups[c] || []).push(a); });
                            const nodes = [];
                            Object.keys(groups).sort().forEach((cat) => {
                                nodes.push(<MenuItem key={`h-${cat}`} disabled sx={{ fontWeight: 700, fontSize: '0.65rem', color: '#64748B', opacity: '1 !important', fontFamily: PP }}>{cat}</MenuItem>);
                                groups[cat].forEach((a) => {
                                    const priceNum = parseFloat(String(a.price ?? '').replace(/[^\d.+]/g, ''));
                                    const priceLabel = String(a.price ?? '').includes('+') ? a.price : (Number.isFinite(priceNum) ? priceNum.toLocaleString() : a.price);
                                    const unitTag = a.unitType === 'PerPanel' ? ' /panel' : a.unitType === 'PerYear' ? ' /year' : a.unitType === 'PerItem' ? ' /item' : '';
                                    nodes.push(
                                        <MenuItem key={a.addonId} value={a.addonId} sx={{ fontSize: '0.72rem', fontFamily: PP }}>
                                            {a.itemName} — RM {priceLabel}{unitTag}
                                        </MenuItem>
                                    );
                                });
                            });
                            return nodes;
                        })()}
                    </Select>
                </Box>
            </Box>
        </Box>
    );
}

function OptionCard({ card, label, selected, gradient, basis = 'p50', onBasisChange, hasMd = false, mdKw = 0, sys = {}, onSysChange, battery = '', onBatteryChange, onBatteryBlur, disc = {}, onDiscChange, om = '', onOmChange, omPkg = 'premium', onOmPkgChange, pricing = {}, addonRows, catalog, onAddAddon, onUpdateAddon, onRemoveAddon, mdWarn = null, lang = 'en' }) {
    // ── Derive the original (pre-solar) consumption kWh and both itemised bills ──
    let body = null;
    if (card) {
        const consumptionKwh = Math.round((card.selfConsumed || 0) + (card.importAfterSolar || 0));
        const origBill = calcCommercialLvBill(consumptionKwh);
        const newImportKwh = Math.round(card.importAfterSolar || 0);
        const newBill = calcCommercialLvBill(newImportKwh);
        const exportCredit = card.mode === 'selco' ? 0 : (Math.round(card.exported || 0) * 0.20);
        const newTotalAfterCredit = Math.max(0, newBill.total - exportCredit);

        body = (
            <>
                {/* System spec strip — so the card is self-contained (no scroll to
                    System & Pricing). Pulls each option's own sizing + the shared
                    panel watt / battery. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1.25 }}>
                    <SpecChip>{sys.panels || card.panels} panels × {sys.panelWatt || 640}Wp</SpecChip>
                    <SpecChip>{sys.inverterKw || card.inverterKw}kW {sys.brand || card.brandLabel || card.brand || ''}</SpecChip>
                    {mdKw > 0 && Number(sys.inverterKw || card.inverterKw) > mdKw && <SpecChip>SELCo on excess (&gt;{mdKw}kW MD)</SpecChip>}
                    {Number(battery) > 0 && <SpecChip>{battery}kWh</SpecChip>}
                    {card.stLicenseApplies && <SpecChip>ST License</SpecChip>}
                    {sys.pricePerKwp && <SpecChip>{fmtRM(sys.pricePerKwp)}/kWp</SpecChip>}
                </Box>

                {/* System Details — each card owns its FULL editable system spec
                    (dark-on-gradient). Defaults to the option's auto sizing; edits
                    stick. stopPropagation so editing doesn't toggle selection. */}
                <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1.25 }}>
                    <CardLabel>{tr(lang, 'System Details', '系统配置')}</CardLabel>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Panels</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.panels ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const wpk = (Number(sys.panelWatt) || 640) / 1000;
                                    onSysChange && onSysChange('panels', v);
                                    if (v && sys.pricePerKwp) onSysChange && onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * Number(v) * wpk)));
                                }}
                                inputProps={{ min: 0, step: 1 }} sx={ADDON_INPUT_SX} />
                        </Box>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>System kWp</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.kwp ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const wpk = (Number(sys.panelWatt) || 640) / 1000;
                                    const p = v ? Math.ceil(Number(v) / wpk) : 0;
                                    onSysChange && onSysChange('panels', p ? String(p) : '');
                                    if (p && sys.pricePerKwp) onSysChange && onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * p * wpk)));
                                }}
                                inputProps={{ min: 0, step: 0.01 }} sx={ADDON_INPUT_SX} />
                        </Box>
                    </Box>
                    {sys.panels && sys.panelWatt && (
                        <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.3 }}>
                            Auto: {sys.panels} × {sys.panelWatt}Wp = {(Number(sys.panels) * Number(sys.panelWatt) / 1000).toFixed(2)} kWp
                        </Typography>
                    )}
                    {mdWarn && (
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 600, fontFamily: PP, mt: 0.4,
                            color: mdWarn.csMode === 'selco' ? '#FCD34D' : '#86EFAC' }}>
                            {mdWarn.csMode === 'selco'
                                ? `SELCo — inverter ${mdWarn.neededInv}kW exceeds MD ${mdWarn.atapMaxInv}kW (zero export, self-consumption only)`
                                : `ATAP — inverter ${mdWarn.neededInv}kW within MD ${mdWarn.atapMaxInv}kW`}
                        </Typography>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Panel Watt (Wp)</CardLabel>
                            <Select size="small" fullWidth value={sys.panelWatt || '640'}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSysChange && onSysChange('panelWatt', v);
                                    // kWp auto-recomputes from panels × new wpk via sysFor;
                                    // keep systemCost consistent if a per-kWp price is set.
                                    const p = Number(sys.panels) || 0;
                                    if (p && sys.pricePerKwp) onSysChange && onSysChange('systemCost', String(Math.round(Number(sys.pricePerKwp) * p * (Number(v) / 1000))));
                                }} sx={ADDON_INPUT_SX}>
                                <MenuItem value="620" sx={{ fontSize: '0.72rem', fontFamily: PP }}>620 Wp</MenuItem>
                                <MenuItem value="640" sx={{ fontSize: '0.72rem', fontFamily: PP }}>640 Wp</MenuItem>
                            </Select>
                        </Box>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Inverter kW</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.inverterKw ?? ''}
                                onChange={(e) => onSysChange && onSysChange('inverterKw', e.target.value)}
                                inputProps={{ min: 0, step: 0.1 }} sx={ADDON_INPUT_SX} />
                            {(() => {
                                // LIVE ratio = the kWp/inverter actually shown (recomputes on
                                // manual edits), not the stale candidate ratio. >1.35 → warn.
                                const _k = Number(sys.kwp || card.kwp) || 0;
                                const _i = Number(sys.inverterKw || card.inverterKw) || 0;
                                if (_k <= 0 || _i <= 0) return null;
                                const _r = _k / _i;
                                const _over = _r > 1.351;
                                return (
                                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: _over ? '#FBBF24' : 'rgba(255,255,255,0.65)', fontFamily: PP, mt: 0.4, lineHeight: 1.2 }}>
                                        ratio {_r.toFixed(2)}{_over ? ' — over 1.35, add battery' : ''}
                                    </Typography>
                                );
                            })()}
                        </Box>
                    </Box>
                    {/* Choose inverter TYPE first — it filters the brand options below
                        (Hybrid → GoodWe only; Huawei is String-only). */}
                    <Box sx={{ mt: 1 }}>
                        <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Inverter Type</CardLabel>
                        <ToggleButtonGroup exclusive value={sys.type || 'string'} size="small"
                            onChange={(_, v) => {
                                if (!v) return;
                                onSysChange && onSysChange('type', v);
                                // Hybrid is GoodWe-only — switch brand off Huawei if needed.
                                let b = sys.brand || 'goodwe';
                                if (v === 'hybrid' && b === 'huawei') { b = 'goodwe'; onSysChange && onSysChange('brand', 'goodwe'); }
                                const k = Number(sys.kwp) || 0;
                                const ppk = commercialPricePerKwp(k, b, v, 0);
                                if (ppk > 0 && k > 0) {
                                    onSysChange && onSysChange('pricePerKwp', String(ppk));
                                    onSysChange && onSysChange('systemCost', String(Math.round(ppk * k)));
                                }
                            }}
                            sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.66rem', textTransform: 'none', py: 0.4, px: 1.2, color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' }, '& .Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' } }}>
                            {/* Battery forces Hybrid (String disabled). */}
                            <ToggleButton value="hybrid">Hybrid</ToggleButton>
                            <ToggleButton value="string" disabled={Number(battery) > 0}>String</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                        <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Inverter Brand</CardLabel>
                        <ToggleButtonGroup exclusive value={sys.brand || 'goodwe'} size="small"
                            onChange={(_, v) => {
                                if (!v) return;
                                onSysChange && onSysChange('brand', v);
                                // Brand change re-prices per kWp + refolds system cost.
                                let t = sys.type || 'string';
                                if (v === 'huawei') t = 'string';            // Huawei String-only
                                onSysChange && onSysChange('type', t);
                                const k = Number(sys.kwp) || 0;
                                const ppk = commercialPricePerKwp(k, v, t, 0);
                                if (ppk > 0 && k > 0) {
                                    onSysChange && onSysChange('pricePerKwp', String(ppk));
                                    onSysChange && onSysChange('systemCost', String(Math.round(ppk * k)));
                                }
                            }}
                            sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.66rem', textTransform: 'none', py: 0.4, px: 1.2, color: '#fff', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' }, '& .Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' } }}>
                            {/* Huawei: String-only + ≥100 kWp only → disabled under Hybrid or <100. */}
                            {[['goodwe', 'GoodWe'], ['huawei', 'Huawei']]
                                .map(([k, lbl]) => <ToggleButton key={k} value={k} disabled={k === 'huawei' && (Number(sys.kwp) < 100 || (sys.type || 'string') === 'hybrid')}>{lbl}</ToggleButton>)}
                        </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Per kWp (RM)</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.pricePerKwp ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSysChange && onSysChange('pricePerKwp', v);
                                    onSysChange && onSysChange('systemCost', v && Number(sys.kwp) ? String(Math.round(Number(v) * Number(sys.kwp))) : '');
                                }}
                                inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                        </Box>
                        <Box>
                            <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Panel Total (RM)</CardLabel>
                            <TextField size="small" type="number" fullWidth value={sys.systemCost ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onSysChange && onSysChange('systemCost', v);
                                    onSysChange && onSysChange('pricePerKwp', v && Number(sys.kwp) ? String(Math.round(Number(v) / Number(sys.kwp))) : '');
                                }}
                                inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                        </Box>
                    </Box>
                    {Number(sys.kwp) > 0 && (
                        <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.3 }}>
                            {['goodwe', 'huawei'].map(b => { const p = commercialPricePerKwp(Number(sys.kwp), b, sys.type || 'string', 0); return p > 0 ? `${b === 'goodwe' ? 'GoodWe' : 'Huawei'} RM ${p.toLocaleString()}/kWp` : null; }).filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                        <CardLabel sx={{ fontSize: '0.55rem', mb: 0.3 }}>Add-on Price (RM) — flat</CardLabel>
                        <TextField size="small" type="number" fullWidth value={sys.addOn ?? ''}
                            onChange={(e) => onSysChange && onSysChange('addOn', e.target.value)}
                            inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                    </Box>
                </Box>

                {/* 电池 Battery + 折扣 Discount — per-card inputs (RSC-style).
                    stopPropagation so editing doesn't toggle card selection. */}
                <Box onClick={(e) => e.stopPropagation()} sx={{ mb: 1.25 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box>
                            <CardLabel>{tr(lang, 'Battery (kWh)', '电池')}</CardLabel>
                            <TextField size="small" type="number" fullWidth placeholder={tr(lang, '0 = no battery', '0 = 无电池')}
                                value={battery ?? ''}
                                onChange={(e) => onBatteryChange && onBatteryChange(e.target.value)}
                                onBlur={(e) => onBatteryBlur && onBatteryBlur(e.target.value)}
                                inputProps={{ min: 0, step: 1 }} sx={ADDON_INPUT_SX} />
                            {/* Battery per day: how much it CHARGES (from the solar surplus) vs
                                how much it actually USES (the night load it can discharge into).
                                used < charged ⇒ oversized (a bigger pack can't help); fills < 100%
                                ⇒ the surplus can't fully charge it. (user 2026-06-23) */}
                            {Number(battery) > 0 && card && card.battDailyChargeKwh != null && (() => {
                                const ch = Math.round(card.battDailyChargeKwh || 0);
                                const us = Math.round(card.battDailyUsedKwh || 0);
                                const oversized = us < ch - 0.5;
                                const cantFill = (card.battChargePct ?? 100) < 100;
                                // Excess the battery can't absorb (battery full → surplus left over).
                                // In SELCo that surplus is CLIPPED (wasted) — a bigger battery would
                                // capture it; in ATAP it's exported for SMP/NEM credit (not wasted).
                                const surplus = Math.round(card.dailySurplusKwh || 0);
                                const clip = Math.max(0, surplus - ch);
                                const isSelco = card.mode === 'selco';
                                const showExcess = clip > 1 && !cantFill;   // battery is the bottleneck
                                const warn = oversized || cantFill;
                                const detail = `charges ~${ch}kWh · uses ~${us}kWh${cantFill ? ` · fills ${card.battChargePct}%` : ''}`;
                                const detailCn = `充 ~${ch}kWh · 用 ~${us}kWh${cantFill ? ` · 充到${card.battChargePct}%` : ''}`;
                                return (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4, mt: 0.4 }}>
                                            {warn && <WarningAmberRounded sx={{ fontSize: '0.85rem', color: '#FBBF24', mt: '1px', flexShrink: 0 }} />}
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: warn ? '#FBBF24' : '#86efac', fontFamily: PP, lineHeight: 1.25 }}>
                                                {warn
                                                    ? tr(lang, `Oversized — ${detail}`, `偏大 — ${detailCn}`)
                                                    : tr(lang, `Battery/day: ${detail}`, `电池/天：${detailCn}`)}
                                            </Typography>
                                        </Box>
                                        {showExcess && (
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: isSelco ? '#FBBF24' : '#93C5FD', fontFamily: PP, mt: 0.2, lineHeight: 1.25 }}>
                                                {isSelco
                                                    ? tr(lang, `Excess ~${clip}kWh/day clipped (wasted) — bigger battery captures more`, `多余 ~${clip}kWh/天 被削掉（浪费）— 加大电池可多储`)
                                                    : tr(lang, `Excess ~${clip}kWh/day exported (SMP credit)`, `多余 ~${clip}kWh/天 卖回电网 (SMP)`) }
                                            </Typography>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                        <Box>
                            <CardLabel>{tr(lang, 'Discount (RM or %)', '折扣 (RM 或 %)')}</CardLabel>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <TextField size="small" type="number" placeholder="RM" value={disc.rm ?? ''}
                                    onChange={(e) => onDiscChange && onDiscChange({ rm: e.target.value, pct: '' })}
                                    inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                                <TextField size="small" type="number" placeholder="%" value={disc.pct ?? ''}
                                    onChange={(e) => onDiscChange && onDiscChange({ pct: e.target.value, rm: '' })}
                                    inputProps={{ min: 0, max: 100 }} sx={ADDON_INPUT_SX} />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <CardLabel sx={{ mb: 0 }}>{tr(lang, 'O&M Annual (RM)', '维护费 (RM)')}</CardLabel>
                            <ToggleButtonGroup exclusive size="small" value={omPkg}
                                onChange={(_, v) => { if (v) onOmPkgChange && onOmPkgChange(v); }}
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.56rem', textTransform: 'none', py: 0.1, px: 0.8, lineHeight: 1.2, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '5px !important' }, '& .Mui-selected': { background: 'rgba(255,255,255,0.22) !important', color: '#fff !important' }, '& .Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' } }}>
                                <ToggleButton value="basic" disabled={!basicOmAvailable(sys.kwp)}>Basic</ToggleButton>
                                <ToggleButton value="premium">Premium</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <TextField size="small" type="number" fullWidth placeholder="0"
                            value={om ?? ''} onChange={(e) => onOmChange && onOmChange(e.target.value)}
                            inputProps={{ min: 0 }} sx={ADDON_INPUT_SX} />
                        <Typography sx={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.5)', fontFamily: PP, mt: 0.3 }}>
                            {omPkg === 'basic' ? tr(lang, 'Basic package', '基础配套') : tr(lang, 'Premium package', '高级配套')} · auto from kWp tier · editable
                        </Typography>
                    </Box>
                    {Number(battery) > 0 && (
                        <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)', fontFamily: PP, mt: 0.3 }}>
                            {Number(battery) < 100 ? `${Math.ceil(Number(battery) / 16)} × 16kWh = ${fmtRM(batteryCostFor(battery))}` : `${battery}kWh = ${fmtRM(batteryCostFor(battery))}`}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.5)', fontFamily: PP, fontStyle: 'italic', mt: 0.3 }}>
                        {tr(lang, 'Fill RM or % · system only (panels+inverter), excludes battery & add-ons', 'RM 或 % 任填一项 · 仅适用于系统 (电板+逆变器), 不含电池及配件')}
                    </Typography>
                </Box>

                {/* 原电费 / 新电费 — two white bill boxes */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.25 }}>
                    <BillBox
                        title={tr(lang, 'Original', '原电费')}
                        sub={`${consumptionKwh} kWh`}
                        b={origBill}
                        exportCredit={0}
                    />
                    <BillBox
                        title={tr(lang, 'After Solar', '新电费')}
                        sub={`${newImportKwh} kWh import`}
                        b={newBill}
                        exportCredit={exportCredit}
                        totalOverride={newTotalAfterCredit}
                    />
                </Box>

                {/* Monthly Saving banner */}
                <Box sx={{ p: 1, bgcolor: 'rgba(74,222,128,0.18)', borderRadius: 1.5, textAlign: 'center', border: '1px solid rgba(74,222,128,0.4)' }}>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#86EFAC', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: PP }}>{tr(lang, 'Monthly Saving', '每月省电费')}</Typography>
                    <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.2 }}>{fmtRM(card.monthlySaving)}</Typography>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 600, color: card.mode === 'selco' ? '#FCD34D' : '#86EFAC', fontFamily: PP, mt: 0.3, letterSpacing: '0.04em' }}>
                        {card.mode === 'selco' ? 'SELCo' : 'ATAP'}
                    </Typography>
                </Box>

                {/* Generation split — solar-hour self-use vs off-hour export (the
                    export estimate), then total gen + grid import still needed. */}
                {(() => {
                    const isSelco = card.mode === 'selco';
                    const gen = Math.round(card.generated || 0);
                    const self = Math.round(card.selfConsumed || 0);
                    const selfPct = card.generated > 0 ? Math.round((card.selfConsumed / card.generated) * 100) : 0;
                    const offKwh = Math.round((isSelco ? card.curtailed : card.exported) || 0);
                    const offPct = Math.round(((isSelco ? card.curtailedPct : card.exportPct) || 0) * 100);
                    const imp = Math.round(card.importAfterSolar || 0);
                    const rowL = { fontSize: '0.62rem', color: 'rgba(255,255,255,0.7)', fontFamily: PP };
                    const rowV = { fontSize: '0.62rem', fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono',monospace" };
                    return (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.15 }}>
                                <Typography sx={rowL}>{tr(lang, 'Solar-hour self-use', '日照时段自用')}</Typography>
                                <Typography sx={rowV}>{self.toLocaleString()} kWh · {selfPct}%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.15 }}>
                                <Typography sx={rowL}>{isSelco ? tr(lang, 'Off-hour curtailed', '非时段(弃电)') : tr(lang, 'Off-hour export', '非时段出口')}</Typography>
                                <Typography sx={{ ...rowV, color: isSelco ? '#FCA5A5' : '#86EFAC' }}>{offKwh.toLocaleString()} kWh · {offPct}%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.15, borderTop: '1px solid rgba(255,255,255,0.12)', mt: 0.3, pt: 0.4 }}>
                                <Typography sx={rowL}>{tr(lang, 'Total generation', '总发电量')}</Typography>
                                <Typography sx={rowV}>{gen.toLocaleString()} kWh</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.15 }}>
                                <Typography sx={rowL}>{tr(lang, 'Grid import', '仍需电网')}</Typography>
                                <Typography sx={rowV}>{imp.toLocaleString()} kWh</Typography>
                            </Box>
                        </Box>
                    );
                })()}

                {/* In-card add-on package (RSC-style, on the gradient) */}
                <CardAddons
                    rows={addonRows}
                    catalog={catalog}
                    onAdd={onAddAddon}
                    onUpdate={onUpdateAddon}
                    onRemove={onRemoveAddon}
                    lang={lang}
                />

                {/* 价格明细 · CASH — panel − discount + battery + add-ons = final,
                    then the 80/20 + 6% SST breakdown that drives the commercial quotation. */}
                <Box sx={{ mt: 1.25, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, p: 1.25 }}>
                    <CardLabel sx={{ mb: 0.5 }}>{tr(lang, 'Price · CASH', '价格明细 · CASH')}</CardLabel>
                    <PriceRow label={tr(lang, 'System base', '基础价')} value={fmtRM(pricing.panel || 0)} />
                    {pricing.discAmt > 0 && <PriceRow label={tr(lang, '− Discount', '− 折扣')} value={`− ${fmtRM(pricing.discAmt)}`} discount />}
                    {pricing.battery > 0 && <PriceRow label={tr(lang, '+ Battery', '+ 电池')} value={`+ ${fmtRM(pricing.battery)}`} />}
                    {pricing.addons > 0 && <PriceRow label={tr(lang, '+ Add-ons', '+ 配件')} value={`+ ${fmtRM(pricing.addons)}`} />}
                    <PriceRow label={tr(lang, 'Final (CASH)', '最终价 (CASH)')} value={fmtRM(pricing.cash || 0)} bold />
                    {(() => {
                        const cash = pricing.cash || 0;
                        const p80 = Math.round(cash * 0.80);
                        const p20 = Math.round(cash * 0.20);
                        const sst = Math.round(p20 * 0.06);
                        return (
                            <>
                                <PriceRow label="80% / Part A" value={fmtRM(p80)} />
                                <PriceRow label="20% / Part B" value={fmtRM(p20)} />
                                <PriceRow label="SST 6% (on 20%)" value={fmtRM(sst)} />
                                <PriceRow label={tr(lang, 'Grand Total (incl SST)', '含税总额')} value={fmtRM(cash + sst)} bold />
                                <PriceRow label={tr(lang, '24M CC 0% Instalment', '24M CC 0% 分期')} value={fmtRM(pricing.cc24 || 0)} />
                                <PriceRow label={tr(lang, '36M CC 0% Instalment', '36M CC 0% 分期')} value={fmtRM(pricing.cc || 0)} />
                                <PriceRow label={tr(lang, '60M CC 0% Instalment', '60M CC 0% 分期')} value={fmtRM(pricing.cc60 || 0)} />
                            </>
                        );
                    })()}
                </Box>

                {/* ROI / CASH / 24M CC tiles */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1.25 }}>
                    <Tile label="ROI" value={card.roiYears != null ? `${card.roiYears} yrs` : '—'} />
                    <Tile label="CASH" value={fmtRM(pricing.cash || 0)} />
                    <Tile label="24M CC" value={fmtRM(pricing.cc24 || 0)} />
                </Box>
            </>
        );
    }

    return (
        <div
            style={{
                background: gradient,
                borderRadius: 14,
                padding: 20,
                color: '#fff',
                minHeight: card ? 300 : 'auto',
                border: selected ? '2px solid #fff' : '2px solid transparent',
                boxShadow: selected ? '0 6px 18px rgba(0,0,0,0.28)' : '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'box-shadow 0.15s, border-color 0.15s',
            }}
        >
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff', fontFamily: PP }}>{label}</Typography>
                {card && (
                    <Chip label={`${card.kwp} kWp`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600, fontSize: '0.66rem', fontFamily: PP, height: 22, borderRadius: 1.5 }} />
                )}
            </Box>

            {/* Sizing-basis chooser — sizes this option to a coverage target (50% / 70%),
                plus a contextual 3rd: "MAX MD" when a Max Demand is entered (ATAP-capped),
                or "100%" coverage when there's no MD (the whole system is SELCo). */}
            {onBasisChange && (
            <Box sx={{ mb: 1.25 }}>
                <ToggleButtonGroup exclusive size="small" value={basis}
                    onChange={(_, v) => { if (v && onBasisChange) onBasisChange(v); }} fullWidth
                    sx={{ '& .MuiToggleButton-root': { flex: 1, fontFamily: PP, fontSize: '0.6rem', fontWeight: 700, textTransform: 'none', py: 0.3, px: 0.5, color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.3)', borderRadius: '6px !important' }, '& .Mui-selected': { background: 'rgba(255,255,255,0.22) !important', color: '#fff !important' } }}>
                    <ToggleButton value="p50">~50%</ToggleButton>
                    <ToggleButton value="p70">~70%</ToggleButton>
                    {hasMd
                        ? <ToggleButton value="maxmd">MAX MD</ToggleButton>
                        : <ToggleButton value="p100">100%</ToggleButton>}
                </ToggleButtonGroup>
            </Box>
            )}

            {card ? body : (
                <Typography sx={{ fontStyle: 'italic', fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontFamily: PP, py: 1 }}>
                    Enter a bill to size this option.
                </Typography>
            )}
        </div>
    );
}

export default function CscCombinedTab({ sharedPhaseMode = 'single', setSharedPhaseMode, lang = 'en' }) {
    const { user } = useUser();
    const navigate = useNavigate();
    const [submitBusy, setSubmitBusy] = useState(false);
    const submitGuardRef = useRef(false);
    const [lastSubmittedSig, setLastSubmittedSig] = useState(null);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const doClearData = () => { try { sessionStorage.removeItem(CSC_DRAFT_KEY); } catch {} window.location.reload(); };
    const [bill, setBill] = useState('');
    const [tariffRate, setTariffRate] = useState(0.515);
    const [operatingDays, setOperatingDays] = useState(7);   // days per WEEK (default 7; × ~4.345 weeks = workdays/month)
    const [opStart, setOpStart] = useState(9);
    const [opEnd, setOpEnd] = useState(17);
    const [maxDemand, setMaxDemand] = useState('');
    // Mode AUTO from Max Demand: MD entered = ATAP (export, validated vs demand); blank =
    // SELCo (zero export). No checkbox (user 2026-06-23).
    const csMode = (parseFloat(maxDemand) || 0) > 0 ? 'atap' : 'selco';
    // (fullSelfUse flag removed — daytime % follows hours by default / manual override; the
    // 100%-self-use case is just daytime % = 100, see _fullSelf derivation below.)
    // Non-daytime usage % — the share of monthly kWh consumed OUTSIDE solar hours
    // (nights, weekends, non-operating days) that is ALWAYS grid-imported. Solar
    // can't cover it, so there's always a residual bill (export only offsets part).
    const [nonDayPct, setNonDayPct] = useState('20');   // legacy (kept for draft back-compat); calc uses _offHour
    // Daytime usage % — the share of consumption during solar hours. Default 100%
    // (typical commercial: all usage in the morning/daytime). Editable in manual mode;
    // when "follow operating hours" is on it auto-derives from the operating window.
    // (user 2026-06-23, replaces the inverted Non-daytime field)
    const [dayUsagePct, setDayUsagePct] = useState('');   // blank = follow operating hours; type to override
    const [brand, setBrand] = useState('goodwe');

    // Which option (A or B) the Section-2 Proposal + Section-3 Quotation/Forms/Create
    // Sales are generated for. Chosen via the chooser at the top of Section 2 (RSC
    // parity — choose the option, the package below follows it). Was hardcoded to 'a'
    // (user 2026-06-22). Each option keeps its own basis/battery/discount/add-ons.
    const [copied, setCopied] = useState(false);
    const [selectedOpt, setSelectedOpt] = useState('a');

    // ── Per-card add-on catalog (ported, simplified, from RSC AI BESS 360) ──
    // `addons` = shared catalog; `cscAddons` = per-option-key map of picked rows
    // ('a' / 'b'). Each card gets its own editable add-on table; rows fold into
    // that card's price + (for the SELECTED option) the 3 generated outputs.
    const [addons, setAddons] = useState([]);
    const [cscAddons, setCscAddons] = useState({});
    useEffect(() => {
        let c = false;
        getAddons().then(r => { if (!c) setAddons(r.data || []); }).catch(() => {});
        return () => { c = true; };
    }, []);

    const addonsForKey = (key) => cscAddons[key] || [];
    const addAddonRow = (key, addonId) => {
        const cat = (addons || []).find(a => a.addonId === addonId);
        if (!cat) return;
        const rowId = `r-${addonId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const row = { ...cat, rowId, _qty: 1 };
        setCscAddons(prev => ({ ...prev, [key]: [...(prev[key] || []), row] }));
    };
    const updateAddonRow = (key, rowId, patch) => setCscAddons(prev => ({ ...prev, [key]: (prev[key] || []).map(r => r.rowId === rowId ? { ...r, ...patch } : r) }));
    const removeAddonRow = (key, rowId) => setCscAddons(prev => ({ ...prev, [key]: (prev[key] || []).filter(r => r.rowId !== rowId) }));
    const addonTotalForKey = (key) => (cscAddons[key] || []).reduce((sum, a) => {
        const qty = (a.unitType === 'PerPanel' || a.unitType === 'PerItem' || a.unitType === 'PerYear')
            ? (a._qty != null && a._qty !== '' && Number.isFinite(Number(a._qty)) ? Math.max(0, Number(a._qty)) : 1) : 1;
        return sum + computeAddonAmount(a, qty).total;
    }, 0);

    // ── Per-card battery / discount / O&M (RSC-style, each option carries its own) ──
    // Battery: free-type kWh, <100 snaps UP to nearest 16-kWh unit, ≥100 as typed
    // (same method as the old shared field). Discount: RM or % on the PANEL system
    // only (excludes battery + add-ons), mirroring RSC. O&M: annual maintenance fee.
    // Option B = Max MD: defaults to ONE 16 kWh battery (per user — the max-saving
    // option always proposes at least 1 battery to capture peak surplus). Editable;
    // clearing to 0 sticks. Option A (~50%) defaults to no battery.
    const [cscBattery, setCscBattery] = useState({ a: '', b: '16' });
    const [cscDisc, setCscDisc] = useState({});   // { a:{rm,pct}, b:{rm,pct} }
    const [cscOm, setCscOm] = useState({ a: '', b: '' });
    const [cscOmPkg, setCscOmPkg] = useState({ a: 'premium', b: 'premium' });   // 'basic' | 'premium'
    // Raw write while typing (no rounding) so digits don't jump mid-edit.
    const setCardBatteryRaw = (key, v) => setCscBattery(prev => ({ ...prev, [key]: v }));
    // Snap on blur: <100 → ceil(n/16)*16, ≥100 kept as typed.
    const setCardBattery = (key, v) => {
        const n = parseFloat(v);
        const val = (v === '' || isNaN(n)) ? v : String(n > 0 && n < 100 ? Math.ceil(n / 16) * 16 : n);
        setCscBattery(prev => ({ ...prev, [key]: val }));
        // Battery present → force GoodWe Hybrid pricing for that card (the override
        // pricePerKwp/systemCost won't auto-update, so recompute them here). Single
        // card layout: only key 'a' (optA) exists; optB is not present.
        const n2 = parseFloat(val) || 0;
        if (n2 > 0) {
            const s = sysFor(key, optA);
            const k = Number(s.kwp) || 0;
            const ppk = commercialPricePerKwp(k, 'goodwe', 'hybrid', 0);
            if (ppk > 0 && k > 0) setCscSys(prev => ({ ...prev, [key]: { ...(prev[key] || {}), brand: 'goodwe', type: 'hybrid', pricePerKwp: String(ppk), systemCost: String(Math.round(ppk * k)) } }));
        }
    };
    const discFor = (key) => cscDisc[key] || {};
    const setCardDisc = (key, patch) => setCscDisc(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
    const setCardOm = (key, v) => setCscOm(prev => ({ ...prev, [key]: v }));
    // Switching package re-seeds O&M from that package's tier (clears manual override).
    const setCardOmPkg = (key, pkg, kwp) => {
        setCscOmPkg(prev => ({ ...prev, [key]: pkg }));
        setCscOm(prev => ({ ...prev, [key]: '' }));   // '' = follow the tier default for the new pkg
    };

    // Per-card system details. Each option owns its own; overrides default to the
    // option's auto sizing (card.kwp/panels/inverterKw/brand/cashPrice). O&M defaults
    // from the kWp tier table (omForKwp) until manually overridden.
    const [cscSys, setCscSys] = useState({ a: {}, b: {} });
    const setSysField = (key, field, value) => setCscSys(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
    // Re-size on any sizing-input change: drop the manual panel / inverter / cost
    // overrides so a new bill (or operating-days / hours / daytime / MD) RE-PROPOSES the
    // system instead of sticking at the previously-edited panel count. Brand / panel-watt
    // / price-per-kWp are kept. (user 2026-06-24 — "after I edit panels, change bill, it stuck")
    const _firstSizeRef = useRef(true);
    useEffect(() => {
        if (_firstSizeRef.current) { _firstSizeRef.current = false; return; }   // keep restored draft on mount
        setCscSys(prev => {
            const strip = (o) => { if (!o) return {}; const { panels, inverterKw, systemCost, kwp, ...rest } = o; return rest; };
            return { a: strip(prev.a), b: strip(prev.b) };
        });
        // Also reset Option A's battery to its default (none) so it recomputes with the
        // new bill — Option B's battery auto-recomputes via its own effect. (user 2026-06-24)
        setCscBattery(prev => (prev.a === '' ? prev : { ...prev, a: '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bill, operatingDays, opStart, opEnd, dayUsagePct, maxDemand]);
    const sysFor = (key, card) => {
        const o = cscSys[key] || {};
        // Panels is the source of truth; kWp is ALWAYS derived = panels × (watt/1000).
        // Derive panels from the card's kWp (round UP to a whole panel), then snap kWp
        // back so it's an exact panel multiple. o.kwp is never stored / read.
        const panelWatt = o.panelWatt != null ? o.panelWatt : '640';
        const wpk = (Number(panelWatt) || 640) / 1000;
        const panelsNum = o.panels != null ? Number(o.panels)
            : (card?.kwp != null ? Math.ceil(Number(card.kwp) / wpk) : (card?.panels != null ? Number(card.panels) : 0));
        const panels = panelsNum ? String(panelsNum) : '';
        const kwp = panelsNum ? String(+(panelsNum * wpk).toFixed(2)) : (card?.kwp != null ? String(card.kwp) : '');
        const inverterKw = o.inverterKw != null ? o.inverterKw : (card?.inverterKw != null ? String(card.inverterKw) : '');
        let brand = o.brand != null ? o.brand : (card?.brand || 'goodwe');
        let type  = o.type  != null ? o.type  : 'string';
        // Battery requires Hybrid (GoodWe only); Huawei is String only.
        if (Number(cscBattery[key]) > 0) { brand = 'goodwe'; type = 'hybrid'; }
        if (brand === 'huawei') type = 'string';
        const systemCost = o.systemCost != null ? o.systemCost : (card?.cashPrice != null ? String(Math.round(card.cashPrice)) : '');
        const pricePerKwp = o.pricePerKwp != null ? o.pricePerKwp : ((card?.cashPrice && card?.kwp) ? String(Math.round(card.cashPrice / card.kwp)) : '');
        const addOn = o.addOn != null ? o.addOn : '';
        // O&M defaults from the kWp tier for the chosen package (basic/premium);
        // a manual entry in cscOm overrides it.
        const omPkg = cscOmPkg[key] || 'premium';
        const om = (cscOm[key] != null && cscOm[key] !== '') ? cscOm[key] : String(omForKwp(kwp, omPkg));
        return { kwp, panels, panelWatt, inverterKw, brand, type, systemCost, pricePerKwp, addOn, om, omPkg };
    };

    // ---- Shared Project Info form state (mirrors cscProposalTab.jsx for Task 5 reuse) ----
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [siteState, setSiteState] = useState('');
    const [sitePhotos, setSitePhotos] = useState([]);
    const [includeOutright, setIncludeOutright] = useState(true);
    const [includeSARE, setIncludeSARE] = useState(true);
    const [includeLoan, setIncludeLoan] = useState(true);
    const [includeCA, setIncludeCA] = useState(true);
    const [includeITA, setIncludeITA] = useState(false);
    const [itaTouched, setItaTouched] = useState(false);
    const [sareTariffOverride, setSareTariffOverride] = useState('');
    const [sareTouched, setSareTouched] = useState(false);
    const [loanPrincipal, setLoanPrincipal] = useState('');
    const [loanRate, setLoanRate] = useState('5.0');
    const [loanTermMonths, setLoanTermMonths] = useState('84');
    const [sales, setSales] = useState({ name: '', title: 'Solar Consultant', email: '', whatsapp: '', photoUrl: '' });

    // ── Commercial-form fields (feed ATAP Contract / Declaration / Insurance) ──
    // Company details
    const [refNo, setRefNo] = useState('');
    const [companyTin, setCompanyTin] = useState('');
    const [companyOffice, setCompanyOffice] = useState('');
    const [companyFax, setCompanyFax] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    // PIC (person-in-charge)
    const [picSalutation, setPicSalutation] = useState('');
    const [picName, setPicName] = useState('');
    const [picMykad, setPicMykad] = useState('');
    const [picPosition, setPicPosition] = useState('');
    const [picCitizenship, setPicCitizenship] = useState('');
    const [picMobile, setPicMobile] = useState('');
    const [picEmail, setPicEmail] = useState('');
    // Authorised / alternate person
    const [altSalutation, setAltSalutation] = useState('');
    const [altName, setAltName] = useState('');
    const [altMykad, setAltMykad] = useState('');
    const [altPosition, setAltPosition] = useState('');
    const [altRelationship, setAltRelationship] = useState('');
    const [altCitizenship, setAltCitizenship] = useState('');
    const [altMobile, setAltMobile] = useState('');
    const [altEmail, setAltEmail] = useState('');
    // Warranty / terms
    const [operationWarranty, setOperationWarranty] = useState('1 year');
    const [defectWarranty, setDefectWarranty] = useState('1 set');
    const [insuranceAmount, setInsuranceAmount] = useState('');

    // ── Prepared-By agent picker (Group → Sales Agent Name → auto Position) ──
    // Defaults to the logged-in agent's group + the agent themselves, but is
    // editable. Picking an agent sets sales.name + sales.title (which the 3
    // generators already read). Referral-role agents are filtered out.
    const [agentGroups, setAgentGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [groupAgents, setGroupAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const filterAgents = (list) => (list || []).filter(a => !(a.userRoles || []).some(ur => ur.roleName === 'Referral'));
    // Proposal / template path uses the legacy 'en'/'cn'/'my' codes (template files
    // are astern-proposal-csc-cn.html / -my.html). The header prop `lang` is
    // 'en'/'zh'/'ms', so map it once here. WhatsApp keeps the raw prop `lang`.
    const proposalLang = lang === 'zh' ? 'cn' : lang === 'ms' ? 'my' : 'en';

    const { atapPrices } = useRscPricing('Residential Pricing', sharedPhaseMode, 999);

    // Effective daytime usage % → off-hour baseline + the 100%-self-use flag the saving
    // calc consumes. Manual mode (default): the typed daytime %. "Follow operating hours"
    // mode (!fullSelfUse): derive it from the operating window's overlap with the solar
    // window (08:00–18:00). 100% daytime in manual mode ⇒ full self-use. (user 2026-06-23)
    // Daytime usage % FOLLOWS the operating hours by default = operating-hours-within the
    // solar window (08:00–18:00) ÷ operating hours (9–17 = 8÷8 = 100%). A manually typed
    // dayUsagePct overrides it; clear the field to go back to following. No checkbox.
    // Operating DAYS aren't mixed into the % — weekends are full export, handled by the
    // saving calc's off-days (user 2026-06-23).
    const _computedDayPct = (() => { const oh = Math.max(0, opEnd - opStart); if (oh <= 0) return 100; const ov = Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8)); return Math.max(0, Math.min(100, Math.round(ov / oh * 100))); })();
    const _effDayPct = String(dayUsagePct).trim() !== '' ? Math.max(0, Math.min(100, parseFloat(dayUsagePct) || 0)) : _computedDayPct;
    const _offHour = Math.max(0, Math.min(1, (100 - _effDayPct) / 100));
    const _fullSelf = _effDayPct >= 100;
    // When a manual daytime % is typed, nudge the operating window (opEnd) so the hours
    // roughly reflect it — a rough estimate is fine (user 2026-06-23). Empty = follow (no
    // reverse sync). ≥100% leaves the window (it's already all-daytime).
    const syncHoursFromDayPct = (raw) => {
        const s = String(raw).trim();
        if (s === '') return;
        const d = Math.max(0, Math.min(100, parseFloat(s) || 0));
        const maxOverlap = Math.max(0, 18 - Math.max(opStart, 8));
        if (d >= 100 || maxOverlap <= 0) return;
        setOpEnd(Math.max(opStart + 1, Math.min(24, Math.round(opStart + maxOverlap / (d / 100)))));
    };

    const { proposals, candidates, snap96, breakdown: sizingBreakdown } = useCscBillProposal({
        residentialRows: atapPrices,
        monthlyBill: parseFloat(bill) || 0,
        tariffRate, operatingDays, opStart, opEnd,
        maxDemandKw: parseFloat(maxDemand) || 0,
        phaseMode: sharedPhaseMode, mode: csMode, brand,
        invType: (parseFloat(cscBattery.a) > 0) ? 'hybrid' : 'string',
        // Battery feeds the SELCo storage model (excess solar stored vs curtailed).
        battKwhTotal: parseFloat(cscBattery.a) || 0,
        // Non-daytime baseline (nights/weekends/non-operating days) is ALWAYS imported —
        // solar can't cover it, so the bill never reaches RM 0 unless "100% self-use" is on.
        offHourBaselinePct: _offHour,
        fullSelfUse: _fullSelf,
    });

    // Two fixed option cards (no toggle): A = ~50% bill saving, B = Max (MD-sized).
    const [optBasis, setOptBasis] = useState({ a: 'p50', b: 'maxmd' });
    const BASIS_LABEL = { p50: '~50% Bill Saving', p70: '~70% Bill Saving', p100: '100% Coverage', maxmd: 'Max', selco: 'SELCo (Zero Export)' };
    const BASIS_LABEL_ZH = { p50: '约省 50% 电费', p70: '约省 70% 电费', p100: '100% 覆盖', maxmd: '最大', selco: 'SELCo (零出口)' };
    const basisLabel = (b) => (lang === 'zh' ? (BASIS_LABEL_ZH[b] || BASIS_LABEL[b]) : BASIS_LABEL[b]);
    // The "Max" option uses the SAME sizing method in both modes (per user): the
    // covering + 72 kW-cap + fill-to-1.35 logic (maxMdCard). With no MD entered,
    // SELCo and ATAP size identically — e.g. a 6k bill → 96 kWp, a 7k bill → 134 kWp.
    // Normalise a stored basis to the currently-valid set so flipping the mode
    // never leaves a dangling selection.
    const isAtap = csMode === 'atap';
    const normBasis = (b) => isAtap
        ? (b === 'p100' || b === 'selco' ? 'maxmd' : b)
        : (b === 'selco' ? 'maxmd' : b);
    // Smallest NEM system whose monthly saving covers ≥ frac of the original TNB
    // bill; falls back to the candidate closest to the target. Returns the
    // SMALLEST system that hits the target — NO inverter-fill / ballooning.
    // (Per user: stop over-designing — a RM350 bill must not propose 20 kWp.)
    // Largest license-free (≤72 kW) candidate ≈ the 96 kWp package, and the kWh a
    // 100% offset would need — used to keep % cards out of the 72–100 kW dead zone.
    const max72Card = (candidates || []).filter(c => c.inverterKw <= 72).reduce((b, c) => (!b || c.kwp > b.kwp ? c : b), null);
    const monthlyKwhTargetC = (parseFloat(bill) || 0) / (tariffRate || 1);
    const pickPct = (frac) => {
        const list = candidates || [];
        if (!list.length) return null;
        const billN = parseFloat(bill) || 0;
        // Target the % of bill saved by GENERATION (gross), NOT net of the ST license
        // fee — a "~50%" card sizes to offset 50% of the bill, it does not over-size
        // to recover the RM800 license (per user: 14k → 133 kWp, not 149 kWp).
        // Bill-only sizing: % of bill measured by the REFERENCE saving (full generation
        // × tariff), so the kWp doesn't shrink/grow with operating days (see pickByGross).
        const gpct = (c) => billN > 0 ? ((Number(c.generated) || 0) * (tariffRate || 0)) / billN : (c.savingPct || 0);
        const pool = list.filter(c => gpct(c) >= frac && c.roiYears != null);
        const picked = pool.length
            ? pool.reduce((b, c) => (c.panels < b.panels ? c : b), pool[0])
            : list.reduce((b, c) => (Math.abs(gpct(c) - frac) < Math.abs(gpct(b) - frac) ? c : b), list[0]);
        // Dead-zone cap (same rule as Option B): if this jumped to a 100 kW inverter
        // but the % target only needs a 72–100 kW system, pull back to the 72 kW /
        // ~96 kWp package — don't go up unless ≥100 kW is genuinely needed.
        if (picked && picked.inverterKw > 72 && max72Card) {
            const genPerKwp = list[0]?.kwp > 0 ? list[0].generated / list[0].kwp : 0;
            const reqKwp = genPerKwp > 0 ? (frac * monthlyKwhTargetC) / genPerKwp : 0;
            const natInv = Math.max(5, Math.ceil(reqKwp / 1.35 / 5) * 5);
            // Break-free at 90 (was 100): only a 73–89 kW natural need pulls back to the
            // 72 kW / ~96 kWp package; ≥90 kW keeps the bigger system.
            if (natInv < 90) return max72Card;
        }
        return picked;
    };

    // MAX MD caps at the SAVING PLATEAU — the smallest system that already reaches
    // peak monthly saving. Beyond it, extra panels just curtail / hit the export
    // cap (and start paying the ST-license fee), so net saving stops rising — no
    // point ballooning to the full Max-Demand size. proposals.maxSaving picks the
    // smallest INVERTER whose generation covers consumption (so the inverter isn't
    // oversized → no 20 kWp on a small bill). Then fill panels to the MOST that
    // inverter can carry at ratio <1.35 — per user, "max panels the kW can support".
    const fillInverter = (card) => {
        if (!card) return card;
        const same = (candidates || []).filter(c => c.inverterKw === card.inverterKw);
        return same.length ? same.reduce((b, c) => (c.panels > b.panels ? c : b), card) : card;
    };
    const maxMdCard = fillInverter(proposals?.maxSaving || null);

    // "Propose 96 kWp straight if near" — when a NEM basis lands at ~88–96 kWp and
    // the MD can carry a 72 kW inverter (snap96 exists), snap to the standard
    // 96 kWp / 72 kW ST-free package. SELCo is left alone.
    const snapNear96 = (card, basis) => {
        if (basis === 'selco' || !card || !snap96) return card;
        return (card.kwp >= 88 && card.kwp < 96) ? snap96 : card;
    };
    const cardForBasis = (basis) => {
        const base = basis === 'maxmd' ? maxMdCard
            : basis === 'selco' ? (proposals?.selco || null)
            : basis === 'p100' ? pickPct(1.00)
            : basis === 'p70' ? pickPct(0.70)
            : pickPct(0.50);
        return snapNear96(base, basis);
    };

    // ── Option A sizing: bill → TARGET monthly saving lookup (user 2026-06-22) ──
    // The agent's manual method: read a target monthly saving off the bill, then size
    // the SMALLEST system whose generation × tariff (grossMonthlySaving) reaches it.
    // 640 Wp panels, capped at the inverter's 1.35 ratio (the candidate list already
    // respects that cap). Bills under RM 1.4k cover ~the full bill ("Max"). The 5,000
    // target lands at ~96 kWp @ 0.515 — matches the hand-quote. Option A's default
    // "~50%" basis now uses this table; picking 70%/Max MD/SELCo still overrides it.
    const targetSavingForBill = (billN) => {
        if (billN <= 0) return 0;
        // Single curve (user 2026-06-24):
        //   • ~87% of the bill for small/mid bills → e.g. RM2k ≈ 52 panels (~33 kWp).
        //   • FLOORED at 5,000 = the 96.64 kWp license-free max → RM7k/7.5k land at 96.64.
        //   • capped at 65% of the bill → once 65% > 5k (~RM7.7k+) it climbs past 96 kWp,
        //     and the ≥90 kW break-free grows the big bills (RM20k/30k → ~128 kWp+).
        // pickByGross then takes the smallest 5 kW inverter that reaches the target and
        // fillInverter fills panels to that inverter's max kWp.
        return Math.min(Math.round(billN * 0.87), Math.max(5000, Math.round(billN * 0.65)));
    };
    const pickByGross = (targetRm) => {
        const list = (candidates || []).filter(c => c.roiYears != null);
        if (!list.length) return null;
        // Size from the BILL ONLY: match the target against the REFERENCE saving =
        // full generation × tariff (i.e. 100% daytime, every day self-used), which is
        // operating-days-INDEPENDENT — so the kWp is fixed by the bill. The displayed
        // saving then floats with the customer's actual operating days (fewer days →
        // less saving), instead of the engine growing the kWp to pin the saving.
        // (user 2026-06-24: "bill decides the kWp; operating days lower → saving lesser")
        const refSave = (c) => (Number(c.generated) || 0) * (tariffRate || 0);
        const reach = list.filter(c => refSave(c) >= targetRm);
        return reach.length
            ? reach.reduce((b, c) => (c.panels < b.panels ? c : b), reach[0])   // smallest that REACHES the target
            : list.reduce((b, c) => (refSave(c) > refSave(b) ? c : b), list[0]); // none reaches → biggest generation
    };
    const billTargetCard = () => {
        const t = targetSavingForBill(parseFloat(bill) || 0);
        // The target picks the INVERTER; then FILL that inverter to its max panels at the
        // 1.35 ratio — don't leave inverter headroom unused (user: "use the inverter max
        // panels"). e.g. 5,000 target → 72 kW inverter → 151 panels = 96.64 kWp (= floor
        // of 72×1.35÷0.64; 152 panels would exceed 97.2 kWp). (2026-06-23)
        const card = t === 'max' ? pickPct(1.00) : pickByGross(t);
        return fillInverter(card);
    };

    const basisA = normBasis(optBasis.a);
    const optA = basisA === 'p50' ? billTargetCard() : cardForBasis(basisA);
    const basisB = normBasis(optBasis.b);
    // Option B = the SAME system as Option A, just WITH battery. A is the no-battery
    // option; B is the with-battery comparison (user 2026-06-23). B's battery count is
    // auto-set by the saving rule in the effect below.
    const optB = optA;
    // Auto-battery for Option B: propose the largest battery (16 kWh units, max 2 = 32 kWh)
    // the day's solar SURPLUS (generation − daytime self-use) can FULLY charge. Surplus
    // that covers 16 → propose 16; ≥ 32 → 32; under 16 → none. A stays battery-free.
    // (user 2026-06-23) Keyed on the sizing/usage inputs so there's no render loop.
    useEffect(() => {
        const a = optA;
        const billN = parseFloat(bill) || 0;
        if (!a || billN <= 0) return;
        const monthlyKwh = tariffRate > 0 ? billN / tariffRate : 0;
        const e = estimateMonthlySaving({ kwp: a.kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: 0, offHourBaselinePct: _offHour });
        // At least 1 battery for Option B (16 kWh); up to 2 (32 kWh) if a day's surplus
        // can fully charge it. If surplus < 16, B still gets 1 (the fill-% warning shows).
        const units = Math.max(1, Math.min(2, Math.floor((e.dailySurplusKwh || 0) / 16)));
        const bVal = String(units * 16);
        setCscBattery(prev => prev.b === bVal ? prev : { ...prev, b: bVal });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optA?.kwp, bill, _offHour, operatingDays, opStart, opEnd, tariffRate]);
    const selectedCard = selectedOpt === 'b' ? optB : optA;

    // Single source of price truth for a card: panel − discount + battery + add-ons.
    // Discount applies to the PANEL system ONLY (excludes battery + add-ons). Each
    // card reads its OWN per-card system state (sysFor): editable System Price +
    // flat Add-on Price. 36M CC = CASH ÷ 0.915 (RSC factor).
    const cardPricing = (key, card) => {
        const s = sysFor(key, card);
        const panel = Number(s.systemCost) || 0;
        const battery = batteryCostFor(cscBattery[key]);
        const addons = addonTotalForKey(key) + (Number(s.addOn) || 0);
        const d = cscDisc[key] || {};
        let discAmt = 0;
        if (Number(d.rm) > 0) discAmt = Math.min(panel, Number(d.rm));
        else if (Number(d.pct) > 0) discAmt = Math.round(panel * Number(d.pct) / 100);
        const panelNet = Math.max(0, panel - discAmt);
        const cash = panelNet + battery + addons;
        const cc24 = Math.round(cash / 0.93);    // 24M CC (CIMB)
        const cc = Math.round(cash / 0.915);     // 36M CC
        const cc60 = Math.round(cash / 0.88);    // 60M CC
        return { panel, panelNet, battery, addons, discAmt, cash, cc24, cc, cc60 };
    };

    // Effective per-card system snapshot for the SELECTED option (auto + override
    // merged). Drives the 3 generators, SARE tier, GITA auto-tick, and math hook.
    const sel = sysFor(selectedOpt, selectedCard);
    // SARE is only for ≥ 40 kWp — auto-untick it (and keep it out of the proposal) when
    // the selected system drops below 40 kWp. (user 2026-06-23)
    useEffect(() => { if ((parseFloat(sel?.kwp) || 0) < 40 && includeSARE) setIncludeSARE(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sel?.kwp]);

    // Auto-default SARE tariff from the kWp tier (40-150→0.36, ≤300→0.32,
    // ≤600→0.30, >600→0.28) until the user overrides it.
    useEffect(() => {
        if (sareTouched) return;
        const k = parseFloat(sel.kwp) || 0;
        if (k < 40) { setSareTariffOverride(''); return; }
        const tier = k <= 150 ? 0.36 : k <= 300 ? 0.32 : k <= 600 ? 0.30 : 0.28;
        setSareTariffOverride(tier.toFixed(2));
    }, [sel.kwp, sareTouched]);

    // GITA (state var includeITA) auto-ticks when kWp > 60 until the user
    // manually toggles the checkbox (itaTouched), after which their choice sticks.
    useEffect(() => {
        if (itaTouched) return;
        setIncludeITA((parseFloat(sel.kwp) || 0) > 60);
    }, [sel.kwp, itaTouched]);

    // Prepared-By picker bootstrap. Load all groups; default group + agent to the
    // logged-in user (still editable). Seed sales.name from the profile so it's
    // populated before the title fetch resolves.
    useEffect(() => {
        let c = false;
        getAllAgentGroups().then(r => { if (!c) setAgentGroups(r.data || []); }).catch(() => {});
        return () => { c = true; };
    }, []);
    useEffect(() => {
        if (!user?.userId) return;
        let c = false;
        setSales(s => ({ ...s, name: handleAgentName(user.firstName, user.lastName, user.alias), photoUrl: absPhoto(user.profilePhotoUrl) }));
        getAgentDetailsByAgentId(user.userId)
            .then(res => {
                const gid = res?.data?.groupId;
                if (c || !gid) return;
                setSelectedGroupId(gid);
                return getAgentsByGroupId(gid).then(ar => {
                    if (c) return;
                    setGroupAgents(filterAgents(ar.data));
                    setSelectedAgentId(user.userId);
                });
            })
            .catch(() => {});
        getSalesTitle(user.userId)
            .then(res => { const t = res?.data?.title; if (!c && t) setSales(s => ({ ...s, title: t })); })
            .catch(() => {});
        return () => { c = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onPickGroup = (gid) => {
        setSelectedGroupId(gid);
        setSelectedAgentId('');
        getAgentsByGroupId(gid).then(r => setGroupAgents(filterAgents(r.data))).catch(() => setGroupAgents([]));
    };
    const onPickAgent = (uid) => {
        setSelectedAgentId(uid);
        const a = groupAgents.find(x => String(x.userId) === String(uid));
        if (a) {
            const photo = a.profilePhotoUrl || (String(uid) === String(user?.userId) ? user?.profilePhotoUrl : '');
            setSales(s => ({ ...s, name: handleAgentName(a.firstName, a.lastName, a.alias), photoUrl: absPhoto(photo) }));
        }
        getSalesTitle(uid).then(r => { const t = r?.data?.title; setSales(s => ({ ...s, title: t || s.title })); }).catch(() => {});
    };

    // ── Proposal math (same call shape as cscProposalTab.jsx) ──
    // Loan/financing must finance the SAME price the outright shows = the selected card's
    // panelNet + battery (the proposal's docSystemCost). sel.systemCost came through empty
    // on auto-sized cards, so the loan principal fell back to kWp × RM1,800 instead of the
    // real price (user 2026-06-23).
    // Full installed price the customer pays = panel (after discount) + battery + add-ons
    // (= P.cash). EVERY financing option (Outright/SARE/Loan/CA) must finance this, so
    // both the battery and the add-ons are counted in for all (user 2026-06-23).
    const _selCash = cardPricing(selectedOpt, selectedCard);
    const _sysCostForMath = Math.round(_selCash.cash || 0);
    const math = useCscProposalMath({
        kwp: parseFloat(sel.kwp) || 0,
        systemCost: _sysCostForMath,
        tariffRate,
        omAnnual: parseFloat(sel.om) || 0,
        includeCA,
        includeITA,
        includeOutright,
        includeSARE,
        includeLoan,
        sareTariffRate: parseFloat(sareTariffOverride) || undefined,
        loanPrincipal: parseFloat(loanPrincipal) || undefined,
        loanRate: parseFloat(loanRate) / 100 || 0.07,
        loanTermMonths: parseInt(loanTermMonths, 10) || 120,
    });

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Monthly saving = REAL TNB bill reduction (Original bill − After-Solar bill, net of
    // export credit) for the displayed system — "Option B" (user 2026-06-08). The SAME
    // figure feeds the breakdown card, WhatsApp, and the proposal summary so all three
    // tally. It auto-respects the 100% self-use toggle (which changes optA's self/export
    // fields). The 20-year TABLE keeps its own generation-revenue model (it's correct).

    // ── Mode follows the explicit ATAP/SELCo master toggle ──────────────────────
    // ATAP → NEM (export credited; MD optional, caps the system when entered).
    // SELCo → zero export (self-consumption only). No MD-derivation any more.
    const _effKwp = Number(sysFor('a', optA)?.kwp) || 0;
    const _md = parseFloat(maxDemand) || 0;
    const _neededInv = _effKwp > 0 ? inverterFromKwp(_effKwp, RATIO_CAP_NO_BATT) : 0;
    const _csMode = csMode === 'selco' ? 'selco' : 'nem';
    const _mdWarn = null;

    // When the user manually edits Panels / System kWp, recompute energy at the
    // effective kWp (derived from sysFor) instead of freezing at the auto-sized optA.
    // kwpEdited is true iff panels were overridden and the resulting kWp differs from
    // the auto-pick by more than 0.01 kWp. When NOT edited, energyForDisplay === optA
    // (zero change in behavior). Either way, the MD-derived _csMode governs the mode.
    const _autoKwp = Number(optA?.kwp) || 0;
    const _kwpEdited = !!optA && _effKwp > 0 && Math.abs(_effKwp - _autoKwp) > 0.01;
    let energyForDisplay = optA;
    if (_kwpEdited) {
        const _monthlyKwh = tariffRate > 0 ? (parseFloat(bill) || 0) / tariffRate : 0;
        const _battA = parseFloat(cscBattery.a) || 0;
        let _e = _csMode === 'selco'
            ? estimateMonthlySavingSELCo({ kwp: _effKwp, tariffRate, monthlyKwh: _monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour })
            : estimateMonthlySaving({ kwp: _effKwp, tariffRate, monthlyKwh: _monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour });
        if (_fullSelf) {
            const _selfFull = Math.min(_e.generated * Math.min(1, (operatingDays || 7) / 7), _monthlyKwh);
            _e = { ..._e, selfConsumed: _selfFull, exported: 0, importAfterSolar: Math.max(0, _monthlyKwh - _selfFull), monthlySaving: _selfFull * tariffRate };
        }
        energyForDisplay = { ...optA, kwp: _effKwp, mode: _csMode, ..._e };
    } else if (optA && _md > 0 && _csMode !== optA.mode) {
        // Not panel-edited, but MD rule forces a different mode than the hook's auto-pick.
        // Recompute energy at _autoKwp with the MD-derived mode so saving is consistent.
        const _monthlyKwh = tariffRate > 0 ? (parseFloat(bill) || 0) / tariffRate : 0;
        const _battA = parseFloat(cscBattery.a) || 0;
        let _e = _csMode === 'selco'
            ? estimateMonthlySavingSELCo({ kwp: _autoKwp, tariffRate, monthlyKwh: _monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour })
            : estimateMonthlySaving({ kwp: _autoKwp, tariffRate, monthlyKwh: _monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: _battA, offHourBaselinePct: _offHour });
        if (_fullSelf) {
            const _selfFull = Math.min(_e.generated * Math.min(1, (operatingDays || 7) / 7), _monthlyKwh);
            _e = { ..._e, selfConsumed: _selfFull, exported: 0, importAfterSolar: Math.max(0, _monthlyKwh - _selfFull), monthlySaving: _selfFull * tariffRate };
        }
        energyForDisplay = { ...optA, mode: _csMode, ..._e };
    }

    const _consumptionKwh = Math.round((energyForDisplay?.selfConsumed || 0) + (energyForDisplay?.importAfterSolar || 0));
    const _origBill = energyForDisplay ? calcCommercialLvBill(_consumptionKwh) : { total: 0 };
    const _newImportKwh = Math.round(energyForDisplay?.importAfterSolar || 0);
    const _newBill = energyForDisplay ? calcCommercialLvBill(_newImportKwh) : { total: 0 };
    const _exportCredit = (energyForDisplay && energyForDisplay.mode !== 'selco') ? Math.round(energyForDisplay.exported || 0) * 0.20 : 0;
    const _newTotalAfterCredit = Math.max(0, (_newBill.total || 0) - _exportCredit);
    const dispSavingMo = energyForDisplay ? Math.max(0, Math.round((_origBill.total || 0) - _newTotalAfterCredit)) : 0;
    const dispCapital = cardPricing('a', optA).cash;
    const dispRoi = (dispSavingMo * 12) > 0 ? Math.round((dispCapital / (dispSavingMo * 12)) * 100) / 100 : null;
    const dispSavingPct = (parseFloat(bill) > 0 && dispSavingMo) ? dispSavingMo / parseFloat(bill) : (energyForDisplay?.savingPct ?? null);
    const optADisplay = energyForDisplay ? { ...energyForDisplay, monthlySaving: dispSavingMo, roiYears: dispRoi, savingPct: dispSavingPct } : energyForDisplay;

    // Card B (Max MD) — same display/saving math as card A above, computed for optB.
    const optBDisplay = (() => {
        if (!optB) return optB;
        const monthlyKwh = tariffRate > 0 ? (parseFloat(bill) || 0) / tariffRate : 0;
        const battB = parseFloat(cscBattery.b) || 0;
        const effKwpB = Number(sysFor('b', optB)?.kwp) || 0;
        const autoKwpB = Number(optB?.kwp) || 0;
        const recomputeB = (kwp) => {
            let e = _csMode === 'selco'
                ? estimateMonthlySavingSELCo({ kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: battB, offHourBaselinePct: _offHour })
                : estimateMonthlySaving({ kwp, tariffRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: battB, offHourBaselinePct: _offHour });
            if (_fullSelf) {
                const selfFull = Math.min(e.generated * Math.min(1, (operatingDays || 7) / 7), monthlyKwh);
                e = { ...e, selfConsumed: selfFull, exported: 0, importAfterSolar: Math.max(0, monthlyKwh - selfFull), monthlySaving: selfFull * tariffRate };
            }
            return e;
        };
        // Always recompute with Option B's battery (battB) — B = A's system + battery, so
        // its energy/saving + battery fill % must reflect that battery. (user 2026-06-23)
        const _kwpB = effKwpB > 0 ? effKwpB : autoKwpB;
        let energyB = { ...optB, kwp: _kwpB, mode: _csMode, ...recomputeB(_kwpB) };
        const consB = Math.round((energyB?.selfConsumed || 0) + (energyB?.importAfterSolar || 0));
        const origB = calcCommercialLvBill(consB);
        const newB = calcCommercialLvBill(Math.round(energyB?.importAfterSolar || 0));
        const credB = (energyB.mode !== 'selco') ? Math.round(energyB.exported || 0) * 0.20 : 0;
        const savingMoB = Math.max(0, Math.round((origB.total || 0) - Math.max(0, (newB.total || 0) - credB)));
        const capitalB = cardPricing('b', optB).cash;
        const roiB = (savingMoB * 12) > 0 ? Math.round((capitalB / (savingMoB * 12)) * 100) / 100 : null;
        const savingPctB = (parseFloat(bill) > 0 && savingMoB) ? savingMoB / parseFloat(bill) : (energyB?.savingPct ?? null);
        return { ...energyB, monthlySaving: savingMoB, roiYears: roiB, savingPct: savingPctB };
    })();

    // Single source of the proposal variable map — used by BOTH the Download PDF
    // path (genProposal) and the Print Preview path (printPreview) so the two
    // outputs are guaranteed identical content.
    // Single price source for the SELECTED option: panel (after discount) +
    // battery folded into systemCost; add-ons (table + flat) passed separately.
    // grand total = systemCost + addOn = P.cash (no double-count).
    const buildProposalMap = () => {
        const P = cardPricing(selectedOpt, selectedCard);
        const docSystemCost = P.panelNet + P.battery;
        return buildCscVarMap({
            customer: { name: customerName, phone: customerPhone, email: customerEmail, address: customerAddress },
            system: { kwp: sel.kwp, systemCost: docSystemCost, tariffRate, omAnnual: Number(sel.om) || 0, inverterKw: Number(sel.inverterKw), inverterBrand: sel.brand, inverterBrands: [sel.brand], panelWatt: Number(sel.panelWatt) || 640, atapPrices, phaseMode: sharedPhaseMode },
            math, sales,
            dates: { issued: todayDMY(), valid: plus30DMY() },
            financing: { loanPrincipal: parseFloat(loanPrincipal) || undefined, includeSARE, includeOutright, includeLoan },
            addOn: P.addons,
            site: siteState, photos: sitePhotos,
            maxDemand: parseFloat(maxDemand) || 0,
            batteryKwh: parseFloat(cscBattery[selectedOpt]) || 0,
            summarySavingMo: dispSavingMo,   // Option B: summary headline = real bill reduction
        });
    };

    const genProposal = async () => {
        if (!customerName.trim()) { setErr('Customer name is required.'); return; }
        if (!sel.kwp || !sel.systemCost) { setErr('System kWp and cost are required.'); return; }
        setErr(''); setBusy(true);
        const map = buildProposalMap();
        const r = await generateCscProposalPdf(map, { lang: proposalLang, includeSARE, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0, customerName, kwp: sel.kwp });
        if (!r.ok) setErr(r.error);
        setBusy(false);
    };

    // ── Print preview (vector-quality PDF via browser Save As PDF) ──
    // Ported from cscProposalTab.jsx; uses setErr + buildProposalMap + the
    // Combined tab's `lang` state. Same template / post-processing / FAB / title
    // injection so the vector output matches the rasterized Download path.
    const printPreview = async () => {
        if (!customerName.trim()) { setErr('Customer name is required.'); return; }
        if (!sel.kwp || !sel.systemCost) { setErr('System kWp and price are required.'); return; }
        setErr('');
        try {
            const cacheBust = `?v=${Date.now()}`;
            const tplUrl = (proposalLang === 'cn' ? '/templates/astern-proposal-csc-cn.html'
                          : proposalLang === 'my' ? '/templates/astern-proposal-csc-my.html'
                          : '/templates/astern-proposal-csc.html') + cacheBust;
            let res = await fetch(tplUrl);
            let tpl = res.ok ? await res.text() : '';
            // Same fallback as generatePdf: also fall back when nginx serves
            // the SPA index.html for a missing localized template (HTTP 200 but no .page divs).
            const looksLikeTemplate = (s) => /class\s*=\s*["']page(\s|["'])/.test(s);
            if (proposalLang !== 'en' && (!res.ok || !looksLikeTemplate(tpl))) {
                res = await fetch(`/templates/astern-proposal-csc.html?v=${Date.now()}`);
                tpl = res.ok ? await res.text() : '';
            }
            if (!res.ok || !looksLikeTemplate(tpl)) {
                throw new Error(`Failed to fetch template (HTTP ${res.status})`);
            }
            const origin = window.location.origin;
            tpl = tpl
                .split("src='./").join(`src='${origin}/templates/`)
                .split('src="./').join(`src="${origin}/templates/`)
                .split("url('./").join(`url('${origin}/templates/`)
                .split('url("./').join(`url("${origin}/templates/`)
                .split('url(./').join(`url(${origin}/templates/`);
            const map = buildProposalMap();
            let populated = postProcessCscProposal(cscSubstitute(tpl, map), { includeSARE, includeOutright, includeLoan, includePhotos: sitePhotos.length > 0 });
            // Inject floating Print button + auto-hide on print
            const printUi = `
<style>
  .csc-print-fab {
    position: fixed; bottom: 24px; right: 24px; z-index: 9999;
    background: linear-gradient(135deg, #1A8CFF, #0F62CC); color: #fff;
    border: none; border-radius: 999px; padding: 12px 22px;
    font: 600 14px/1 'Poppins', -apple-system, sans-serif; letter-spacing: 0.02em;
    box-shadow: 0 8px 20px rgba(15, 98, 204, 0.35); cursor: pointer;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .csc-print-fab:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(15, 98, 204, 0.45); }
  .csc-print-fab:active { transform: translateY(0); }
  .csc-print-fab:disabled { opacity: 0.55; cursor: progress; }
  .csc-print-fab svg { width: 18px; height: 18px; fill: currentColor; }
  @media print { .csc-print-fab { display: none !important; } }
</style>
<button class="csc-print-fab" onclick="window.print()" title="Print or Save as PDF">
  <svg viewBox="0 0 24 24"><path d="M19 8h-1V3H6v5H5a3 3 0 0 0-3 3v6h4v4h12v-4h4v-6a3 3 0 0 0-3-3zM8 5h8v3H8V5zm8 14H8v-5h8v5zm3-7a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"/></svg>
  Print / Save PDF
</button>`;
            populated = populated.replace('</body>', `${printUi}\n</body>`);
            // Browser's Save-as-PDF defaults the filename to the <title>.
            const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
            const kwpLabel = `${Number(sel.kwp) || 0}`;
            const pdfTitle = `${safeName} - ${kwpLabel}KWP Astern Commercial Proposal`;
            populated = populated.replace(/<title>[\s\S]*?<\/title>/, `<title>${pdfTitle.replace(/</g, '&lt;')}</title>`);
            const blob = new Blob([populated], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) { setErr('Pop-up blocked — allow pop-ups for this site, then click again.'); return; }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            console.error(e);
            setErr(`Preview failed: ${e.message || e}`);
        }
    };

    // ── WhatsApp output (RSC-style export chooser + preview) ──────────
    // Mirrors residentialSolarCalculatorV2's export UI: Opt A / Opt B / A+B
    // chooser + bilingual lang toggle + a WhatsApp preview, all fed by CSC's
    // OWN numbers (no RSC builder/pricing imports — only the LAYOUT is shared).
    // (Language comes from the header `lang` prop; single option so selectedOpt = 'a'.)

    // Build the `r`-shape the detailed commercial WhatsApp builder consumes, from
    // this tab's CSC state. Uses the bill-CAPPED card.monthlySaving (not a gross
    // kWp×3.4×30×rate), so the saving can never exceed the bill. Capital cost +
    // add-ons + battery − discount come from cardPricing; commercial tax relief
    // (capital allowance) uses the 24% / 38.8% tier like the System Size tab.
    const buildWaR = (key) => {
        const card = optA;
        if (!card) return null;
        const s = sysFor(key, card);
        const P = cardPricing(key, card);
        const kwpN = parseFloat(s.kwp) || 0;
        const rate = Number(tariffRate) || 0;
        const capitalCost = P.cash;
        const capitalCostRaw = P.cash + P.discAmt;
        // Monthly saving = REAL bill reduction (Option B, dispSavingMo) — same as the
        // breakdown card + proposal summary. 10/20-yr use the table's degraded totals
        // so WhatsApp and the proposal summary agree on the long-term figures too.
        const savingMo = dispSavingMo;
        const savingYr = savingMo * 12;
        const saving10 = Math.round(math?.total10yrOutright || savingYr * 10);
        const saving20 = Math.round(math?.total20yrOutright || savingYr * 20);
        const roi = savingYr > 0 ? capitalCost / savingYr : 0;
        const yieldRate = roi > 0 ? (1 / roi) * 100 : 0;
        const taxPct = kwpN < 60 ? 24 : 38.8;
        const relief = Math.round(capitalCost * (kwpN < 60 ? 0.24 : 0.388));
        const afterTaxCost = capitalCost - relief;
        const roiAfterTax = savingYr > 0 ? afterTaxCost / savingYr : 0;
        const yieldAfterTax = roiAfterTax > 0 ? (1 / roiAfterTax) * 100 : 0;
        const billN = parseFloat(bill) || 0;
        const billSavingPct = billN > 0 ? Math.min((savingMo / billN) * 100, 100) : null;
        const tariffLabel = (TARIFFS.find(t => t.rate === rate) || {}).label || '';
        return {
            kwp: kwpN, panels: parseFloat(s.panels) || 0, kw: parseFloat(s.inverterKw) || 0, battKwh: parseFloat(cscBattery[key]) || 0,
            capitalCost, capitalCostRaw, discount: P.discAmt,
            maintenance: parseFloat(s.om) || 0,
            relief, afterTaxCost, taxPct,
            ppk: 0, battCost: P.battery, rate, tariffLabel, bill: billN, billSavingPct,
            company: (customerName || '').trim().toUpperCase(),
            genMode: 'normal', sareRate: 0,
            savingMo, savingYr, saving10, saving20,
            roi, roiAfterTax, yieldRate, yieldAfterTax,
        };
    };

    const generatedMsg = (() => {
        const r = buildWaR('a');
        if (!r) return '';
        return buildCscWhatsAppMsg(r, lang);
    })();

    const copyText = async (txt) => {
        if (!txt) return;
        try {
            await navigator.clipboard.writeText(txt);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = txt; ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta); ta.select();
            document.execCommand('copy'); document.body.removeChild(ta);
        }
    };
    const onCopyMsg = async () => {
        if (!generatedMsg) return;
        await copyText(generatedMsg);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const onOpenWA = async () => {
        if (!generatedMsg) return;
        await copyText(generatedMsg);
        window.open('https://web.whatsapp.com/', '_blank');
    };

    // ── Commercial Quotation output (backend PDF via formGeneratorApi) ──
    // Mirrors formGenerator.jsx commercial getFormData block: 80/20 split with
    // 6% SST applied to the 20% portion only. Combined tab pays cash, so the
    // selected option's price (+ add-on) drives the split.
    const SST_RATE = 0.06;
    // ── Payment method (drives the Commercial Quotation) ──
    // CC converts the price (36M = cash/0.915, 60M = cash/0.88) → that converted
    // amount flows to the final columns. Cash = as-is. Loan/Leasing don't convert —
    // they print a referral note (the financing party sets the effective rate).
    const [payMethod, setPayMethod] = useState('cash');   // 'cash' | 'cc' | 'loan' | 'leasing'
    const [payTerm, setPayTerm] = useState(24);
    const CC_FACTORS = { 24: 0.93, 36: 0.915, 60: 0.88 };
    const payTermsFor = (m) => m === 'cc' ? [24, 36, 60] : (m === 'loan' || m === 'leasing') ? [36, 60, 84] : [];
    const payFinanceNote = (m) => m === 'loan' ? 'Refer to the effective interest rate charged by the loan party.'
        : m === 'leasing' ? 'Refer to the effective interest rate charged by the leasing party.' : '';
    const payAdjustedPrice = (cash, m, t) => m === 'cc' ? Math.round((Number(cash) || 0) / (CC_FACTORS[t] || 0.915)) : Math.round(Number(cash) || 0);
    const onPayMethod = (m) => { setPayMethod(m); const terms = payTermsFor(m); if (terms.length && !terms.includes(payTerm)) setPayTerm(terms[0]); };
    const [quoteBusy, setQuoteBusy] = useState(false);
    const [formBusy, setFormBusy] = useState('');   // holds the key of the running form

    // Shared commercial payload used by EVERY commercial form (Quotation, ATAP,
    // Declaration, Insurance, All-ZIP). They all consume the same getFormData
    // shape, so one builder feeds them all. Single price source for the SELECTED
    // option (panel − discount + battery + add-ons) → matches proposal + WhatsApp.
    const buildCommercialPayload = () => {
        const P = cardPricing(selectedOpt, selectedCard);
        const cashPrice = P.cash;
        // Quotation is CASH-based (80/20 + 6% SST on the 20%). When CC is chosen, the
        // converted CC figures ride along as an extra "FINAL CC PRICE" reference row.
        const c80 = Math.round(cashPrice * 0.80);
        const c20 = Math.round(cashPrice * 0.20);
        const cSst = Math.round(c20 * SST_RATE);
        const cGrandTotal = c80 + c20 + cSst;
        const isCc = payMethod === 'cc';
        const ccPrice = isCc ? payAdjustedPrice(cashPrice, 'cc', payTerm) : 0;
        const cc80 = isCc ? Math.round(ccPrice * 0.80) : 0;
        const cc20 = isCc ? Math.round(ccPrice * 0.20) : 0;
        const ccSst = isCc ? Math.round(cc20 * SST_RATE) : 0;
        return {
            companyName: (customerName || '').toUpperCase(),
            address: (customerAddress || '').toUpperCase(),
            date: todayDMY(),
            refNo,
            program: 'ATAP',
            kwp: Number(sel.kwp) || 0,
            // Inverter kWac MUST match what the card displays (user 2026-06-21): when
            // the user hasn't manually overridden inverterKw, the card shows the
            // auto-sized ST-capped inverterFromKwp(kWp) (= `_neededInv`), NOT the stale
            // candidate inverterKw. The quotation previously printed the stale value.
            kwac: ((cscSys.a || {}).inverterKw == null && _neededInv > 0) ? _neededInv : (Number(sel.inverterKw) || 0),
            panelQty: Number(sel.panels) || 0,
            panelWatt: String(Number(sel.panelWatt) || 640),
            batteryKwh: Number(cscBattery[selectedOpt]) || 0,
            // Battery qty = physical 16 kWh modules (ceil(kWh/16)), matching the sale
            // line item — a 64 kWh pack is "4 Units", not "1 Unit" (user 2026-06-21).
            batteryQty: (() => { const bk = Number(cscBattery[selectedOpt]) || 0; return bk > 0 ? (bk < 100 ? Math.ceil(bk / 16) : 1) : 0; })(),
            // PIC (person-in-charge) — falls back to the customer contact when blank.
            picSalutation,
            picName: (picName || customerName || '').toUpperCase(),
            picMykad,
            picPosition: (picPosition || '').toUpperCase(),
            picCitizenship,
            picMobile: picMobile || customerPhone || '',
            picEmail: (picEmail || customerEmail || '').toLowerCase(),
            // Authorised / alternate person
            altSalutation,
            altName: (altName || '').toUpperCase(),
            altMykad,
            altPosition: (altPosition || '').toUpperCase(),
            altRelationship,
            altCitizenship,
            altMobile,
            altEmail: (altEmail || '').toLowerCase(),
            // Company details
            companyTin,
            companyOffice,
            companyFax,
            companyEmail: (companyEmail || '').toLowerCase(),
            companyWebsite,
            // Prepared-By (Astern side) signatory — FIXED to the authorising director for
            // all commercial quotations/forms, not the individual salesperson (user 2026-06-23).
            agentName: 'Leng Rong Chen',
            agentPosition: 'Director',
            operationWarrantyYears: operationWarranty || '1 year',
            defectWarrantyYears: defectWarranty || '1 set',
            insuranceAmount: parseInt(insuranceAmount, 10) || 10000,   // backend expects int — blank → default 10,000
            // Commercial pricing 80/20/SST:
            commercialPaymentMethod: payMethod === 'cash' ? 'cash' : `${payMethod}${payTerm}`,   // cash | cc36 | cc60 | loan36 | leasing60 …
            commercialPrice: cashPrice,
            commercialCashPrice: cashPrice,
            commercialPart80: c80,
            commercialPart20: c20,
            commercialSst: cSst,
            commercialGrandTotal: cGrandTotal,
            // CC option (converted) — extra "FINAL CC PRICE" reference row when CC chosen.
            commercialCcTerm: isCc ? payTerm : 0,
            commercialCc80: cc80,
            commercialCc20: cc20,
            commercialCcSst: ccSst,
            commercialFinanceNote: payFinanceNote(payMethod),
        };
    };

    // CSC → create-sales payload. Battery captured as a flagged line item
    // (qty + total kWh); its cost stays folded into FinalPrice (CSC model).
    const buildSalePayload = () => {
        const p = buildCommercialPayload();
        // Same add-on model as RSC: battery is a flagged line, every catalog add-on
        // is its own line, and the per-card flat "Add-on Price" (if any) is a misc
        // line. Price is SPLIT like RSC so add-ons stay traceable: FinalPrice =
        // system-only (panel after discount), AddOnPrice = battery + add-ons. The
        // sale page sums FinalPrice + AddOnPrice = the all-in commercial cash, so
        // nothing double-counts and the battery/incentive tracing works.
        const P = cardPricing(selectedOpt, selectedCard);
        const s = sysFor(selectedOpt, selectedCard);
        const items = [];

        // Battery — ATAP-priced (16 kWh modules @ RM7400 under 100 kWh; per-kWh above).
        const battKwh = Number(cscBattery[selectedOpt]) || 0;
        if (battKwh > 0) {
            const battCost = batteryCostFor(battKwh);
            const qty = battKwh < 100 ? Math.ceil(battKwh / 16) : 1;
            const unit = qty > 0 ? Math.round(battCost / qty) : battCost;
            items.push({
                ItemName: `Battery ${battKwh}kWh`,
                Category: 'Battery',
                IsBattery: true,
                Qty: qty,
                BatteryKwh: battKwh,
                UnitPrice: unit,
                LineTotal: battCost,
                SortOrder: 0,
            });
        }

        // Catalog add-ons (same picker as RSC AI BESS 360; base+rate via computeAddonAmount).
        (addonsForKey(selectedOpt) || []).forEach((r, i) => {
            const qtyBearing = r.unitType === 'PerPanel' || r.unitType === 'PerItem' || r.unitType === 'PerYear';
            const qty = qtyBearing ? (Number(r._qty) || 1) : 1;
            const total = computeAddonAmount(r, qty).total;
            if (total <= 0 && !r.name) return;
            items.push({
                ItemName: r.name || 'Add-on',
                Category: r.category || null,
                IsBattery: false,
                Qty: qty,
                BatteryKwh: null,
                UnitPrice: qty > 0 ? Math.round((total / qty) * 100) / 100 : total,
                LineTotal: total,
                SortOrder: i + 1,
            });
        });

        // Per-card flat "Add-on Price" field (lumpsum), if the agent typed one.
        const flat = Number(s.addOn) || 0;
        if (flat > 0) {
            items.push({
                ItemName: 'Add-on', Category: null, IsBattery: false,
                Qty: 1, BatteryKwh: null, UnitPrice: flat, LineTotal: flat,
                SortOrder: items.length,
            });
        }

        const addOnSum = items.reduce((sum, it) => sum + (Number(it.LineTotal) || 0), 0);

        return {
            PanelQty: p.panelQty,
            Energy: p.kwp,
            // Match the sale form's exact values (COMMERCIAL; CASH / creditCard36|60).
            // Loan/leasing have no sale-form equivalent → fall back to CASH.
            ProductCategory: 'COMMERCIAL',
            Phase: sharedPhaseMode === '3phase' ? '3phase' : 'single',
            OriginalPrice: P.panel,                       // system gross (excl battery/add-ons)
            FinalPrice: P.panelNet,                       // system after discount (excl add-ons)
            AddOnPrice: addOnSum,                          // battery + add-ons = sum of line items
            DiscountByAstern: 0,
            DiscountByAgent: P.discAmt || 0,               // CSC discount is panel-only, manual
            PaymentMethod: payMethod === 'cc' ? `creditCard${payTerm}` : 'CASH',
            DepositOn: null,
            LineItems: items,
            // Snapshot the form payload so the sale can regenerate its forms
            // anywhere (signing link, sale page) without the live calculator.
            FormPayloadJson: JSON.stringify(p),
            // Carry the company/customer details already typed on the CSC form.
            createCustomerRequest: {
                CustomerName: customerName || '',
                PhoneNumber: customerPhone || '',
                Address: customerAddress || '',
                Email: customerEmail || '',
            },
        };
    };

    // Create the Pending commercial sale straight from the form — no dialog.
    // createAnother=true stays on CSC; false navigates to the Sales list.
    const doSubmitSale = async (createAnother) => {
        if (submitGuardRef.current) return;
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        const base = buildSalePayload();
        const sig = JSON.stringify(base);
        if (sig === lastSubmittedSig) {
            if (!window.confirm('You have already created a sale with identical data. Create another duplicate anyway?')) return;
        }
        submitGuardRef.current = true;
        setSubmitBusy(true); setErr('');
        try {
            const res = await createSales({ ...base, AgentId: user?.userId, CreatedBy: user?.username });
            setLastSubmittedSig(sig);
            const newId = res?.data?.salesId;
            // Sale created — drop the saved draft so the calculator starts fresh next time.
            try { sessionStorage.removeItem(CSC_DRAFT_KEY); } catch {}
            const canViewDetail = user?.roleRights?.includes('AllSalesList');
            if (createAnother) alert('Pending sale created. You can create another.');
            else navigate(newId && canViewDetail ? `/accounting/sales/${newId}` : '/sales');
        } catch (e) {
            setErr('Failed to create sale: ' + (e?.response?.data?.message || e?.response?.data || e.message));
        } finally {
            setSubmitBusy(false);
            submitGuardRef.current = false;
        }
    };

    const genQuotation = async () => {
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!sel.systemCost) { setErr('System cost is required.'); return; }
        setErr(''); setQuoteBusy(true);
        try {
            await generateCommercialQuotation2(buildCommercialPayload());
        } catch (e) {
            setErr(`Quotation failed: ${e.message || e}`);
        } finally {
            setQuoteBusy(false);
        }
    };

    // Generic runner for the other commercial forms — per-button busy state keyed
    // by `key`; all share buildCommercialPayload().
    const runCommercialForm = async (key, fn) => {
        if (!customerName.trim()) { setErr('Company / customer name is required.'); return; }
        if (!sel.systemCost) { setErr('System cost is required.'); return; }
        setErr(''); setFormBusy(key);
        try { await fn(buildCommercialPayload()); }
        catch (e) { setErr(`Form failed: ${e.response?.data || e.message || e}`); }
        finally { setFormBusy(''); }
    };

    // ── Draft persistence: keep filled data across page navigation ──
    // Restore once on mount, then save on every input change. Cleared on tab close
    // (sessionStorage) or via the Clear Data button. draftRestoredRef gates the save
    // effect so the initial empty state doesn't overwrite the restored draft.
    const draftRestoredRef = useRef(false);
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(CSC_DRAFT_KEY);
            if (raw) {
                const d = JSON.parse(raw);
                const S = (v, set) => { if (v !== undefined) set(v); };
                S(d.bill, setBill); S(d.tariffRate, setTariffRate);
                S(d.operatingDays, setOperatingDays); S(d.opStart, setOpStart); S(d.opEnd, setOpEnd);
                S(d.maxDemand, setMaxDemand); S(d.brand, setBrand); S(d.nonDayPct, setNonDayPct); S(d.dayUsagePct, setDayUsagePct);
                S(d.cscAddons, setCscAddons); S(d.cscBattery, setCscBattery);
                S(d.cscDisc, setCscDisc); S(d.cscOm, setCscOm); S(d.cscOmPkg, setCscOmPkg);
                S(d.cscSys, setCscSys); S(d.optBasis, setOptBasis);
                S(d.customerName, setCustomerName); S(d.customerPhone, setCustomerPhone);
                S(d.customerEmail, setCustomerEmail); S(d.customerAddress, setCustomerAddress);
                S(d.siteState, setSiteState);
                S(d.includeOutright, setIncludeOutright); S(d.includeSARE, setIncludeSARE);
                S(d.includeLoan, setIncludeLoan); S(d.includeCA, setIncludeCA);
                S(d.includeITA, setIncludeITA); S(d.itaTouched, setItaTouched);
                S(d.sareTariffOverride, setSareTariffOverride); S(d.sareTouched, setSareTouched);
                S(d.loanPrincipal, setLoanPrincipal); S(d.loanRate, setLoanRate);
                S(d.loanTermMonths, setLoanTermMonths);
                S(d.refNo, setRefNo); S(d.companyTin, setCompanyTin);
                S(d.companyOffice, setCompanyOffice); S(d.companyFax, setCompanyFax);
                S(d.companyEmail, setCompanyEmail); S(d.companyWebsite, setCompanyWebsite);
                S(d.picSalutation, setPicSalutation); S(d.picName, setPicName);
                S(d.picMykad, setPicMykad); S(d.picPosition, setPicPosition);
                S(d.picCitizenship, setPicCitizenship); S(d.picMobile, setPicMobile);
                S(d.picEmail, setPicEmail);
                S(d.altSalutation, setAltSalutation); S(d.altName, setAltName);
                S(d.altMykad, setAltMykad); S(d.altPosition, setAltPosition);
                S(d.altRelationship, setAltRelationship); S(d.altCitizenship, setAltCitizenship);
                S(d.altMobile, setAltMobile); S(d.altEmail, setAltEmail);
                S(d.operationWarranty, setOperationWarranty); S(d.defectWarranty, setDefectWarranty);
                S(d.insuranceAmount, setInsuranceAmount);
            }
        } catch { /* ignore corrupt draft */ }
        // Enable saving only AFTER the restored state has been committed, so the
        // save effect's first (mount) run with stale-empty values doesn't overwrite
        // the saved draft.
        setTimeout(() => { draftRestoredRef.current = true; }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!draftRestoredRef.current) return;
        try {
            sessionStorage.setItem(CSC_DRAFT_KEY, JSON.stringify({
                bill, tariffRate, operatingDays, opStart, opEnd,
                maxDemand, nonDayPct, dayUsagePct, brand,
                cscAddons, cscBattery, cscDisc, cscOm, cscOmPkg, cscSys, optBasis,
                customerName, customerPhone, customerEmail, customerAddress, siteState,
                includeOutright, includeSARE, includeLoan, includeCA,
                includeITA, itaTouched, sareTariffOverride, sareTouched,
                loanPrincipal, loanRate, loanTermMonths,
                refNo, companyTin, companyOffice, companyFax, companyEmail, companyWebsite,
                picSalutation, picName, picMykad, picPosition, picCitizenship, picMobile, picEmail,
                altSalutation, altName, altMykad, altPosition, altRelationship,
                altCitizenship, altMobile, altEmail,
                operationWarranty, defectWarranty, insuranceAmount,
            }));
        } catch { /* quota / serialize errors are non-fatal */ }
    }, [bill, tariffRate, operatingDays, opStart, opEnd,
        maxDemand, nonDayPct, dayUsagePct, brand,
        cscAddons, cscBattery, cscDisc, cscOm, cscOmPkg, cscSys, optBasis,
        customerName, customerPhone, customerEmail, customerAddress, siteState,
        includeOutright, includeSARE, includeLoan, includeCA,
        includeITA, itaTouched, sareTariffOverride, sareTouched,
        loanPrincipal, loanRate, loanTermMonths,
        refNo, companyTin, companyOffice, companyFax, companyEmail, companyWebsite,
        picSalutation, picName, picMykad, picPosition, picCitizenship, picMobile, picEmail,
        altSalutation, altName, altMykad, altPosition, altRelationship,
        altCitizenship, altMobile, altEmail,
        operationWarranty, defectWarranty, insuranceAmount]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: '#F8FAFC', fontFamily: PP }}>
            {/* Clear-data confirm — remind that no sale was created yet; offer to create first. */}
            <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>Clear all data?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#475569' }}>
                        You haven't created a sale yet. Clearing will discard all entered data. Create the sale first?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button onClick={() => setClearDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                        Cancel
                    </Button>
                    <Button onClick={() => { setClearDialogOpen(false); doClearData(); }} variant="outlined"
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 700, borderColor: '#EF4444', color: '#EF4444' }}>
                        Clear anyway
                    </Button>
                    <Button onClick={() => { setClearDialogOpen(false); doSubmitSale(false); }} variant="contained"
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 700, boxShadow: 'none', background: 'linear-gradient(135deg,#16A34A,#15803D)' }}>
                        Create Sale
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Top-level controls — laid out one row per control with aligned labels.
                ATAP/SELCo is the MASTER mode switch: ATAP = NEM (MD optional, caps export,
                sizing 50/70/MAX MD); SELCo = zero export (no MD, sizing 50/70/100). */}
            {(() => {
                const LBL = { minWidth: 56, fontSize: '0.72rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, flexShrink: 0 };
                const TG = { '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', py: 0.4, px: 1.6, color: COLORS.sub, borderColor: COLORS.border, borderRadius: '8px !important' } };
                const SEL_BLUE = { '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } };
                const SEL_GREEN = { '& .Mui-selected': { background: 'linear-gradient(135deg,#16A34A,#15803D) !important', color: '#fff !important', borderColor: 'transparent !important' } };
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.75 }}>
                        <Box>
                            <Button size="small" variant="outlined" onClick={() => setClearDialogOpen(true)}
                                sx={{ fontFamily: PP, fontSize: '0.76rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#CBD5E1', color: '#64748B', '&:hover': { borderColor: '#EF4444', color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' } }}>
                                Clear Data
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Typography sx={LBL}>Phase</Typography>
                            <ToggleButtonGroup exclusive size="small" value={sharedPhaseMode}
                                onChange={(_, v) => { if (v && setSharedPhaseMode) setSharedPhaseMode(v); }}
                                sx={{ ...TG, ...SEL_BLUE }}>
                                <ToggleButton value="single">Single Phase</ToggleButton>
                                <ToggleButton value="3phase">3 Phase</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {sharedPhaseMode !== '3phase' && sizingBreakdown?.single1pCapped && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Box sx={{ minWidth: 56, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#B45309', fontFamily: PP }}>
                                    Bill needs &gt;5 kW &mdash; single phase caps at 5 kW. Use 3 Phase.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            })()}

            {/* ───────── Section 1 — CSC Calculator ───────── */}
            <SectionHeader title="Section 1 — CSC Calculator" />

            {/* Full-width LEFT (inputs) / RIGHT (system card + outputs) — RSC-style. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 1fr) minmax(0, 2.4fr)' }, gap: 2, alignItems: 'start' }}>

            {/* LEFT column — shared inputs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {/* White shared-input card — inputs stack one per row in the narrow left
                column so wide fields (Tariff, Operating Hours) aren't truncated. */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                    {/* Company Name up top — same field as the Project Info company name (user 2026-06-23) */}
                    <Box>
                        <Label>Company Name</Label>
                        <TextField fullWidth size="small" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                            placeholder="customer / company"
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                    </Box>
                    <Box>
                        <Label>Monthly Bill (RM)</Label>
                        <TextField fullWidth size="small" type="text" inputMode="numeric"
                            value={bill === '' ? '' : Number(String(bill).replace(/,/g, '')).toLocaleString('en-US')}
                            onChange={(e) => setBill(e.target.value.replace(/[^\d.]/g, ''))}
                            placeholder="e.g. 8,500"
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                    </Box>
                    <Box>
                        <Label>Tariff Type</Label>
                        <Select fullWidth size="small" value={tariffRate} onChange={(e) => setTariffRate(Number(e.target.value))}
                            sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                            {TARIFFS.map(t => (
                                <MenuItem key={t.rate} value={t.rate} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                    {t.label} — RM {t.rate}/kWh
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    {/* No SELCo checkbox — the mode AUTO-decides from Max Demand below:
                        blank = SELCo (zero export), entered = ATAP. (user 2026-06-23) */}
                    <Box>
                        <Label>Maximum Demand (kW) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· blank = SELCo</span></Label>
                        <TextField fullWidth size="small" type="number" value={maxDemand} onChange={(e) => setMaxDemand(e.target.value)}
                            placeholder="blank = SELCo (zero export)"
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                        <Typography sx={{ fontSize: '0.66rem', color: sharedPhaseMode === '3phase' ? COLORS.sub : '#B45309', fontFamily: PP, mt: 0.4 }}>
                            {(parseFloat(maxDemand) || 0) > 0
                                ? 'ATAP — export capped to your Max Demand.'
                                : sharedPhaseMode === '3phase'
                                    ? 'Blank = SELCo (zero export), sized from the bill. Enter MD for ATAP (export to demand).'
                                    : 'Not used on single phase (capped at 5 kW) — switch to 3 Phase to apply.'}
                        </Typography>
                    </Box>
                    {/* No "Follow operating hours" checkbox — operating days/hours are always
                        considered, and daytime % follows them by default. (user 2026-06-23) */}
                    <Box>
                        <Label>Operating Days / week</Label>
                        <TextField fullWidth size="small" type="number" inputProps={{ min: 1, max: 7, step: 1 }}
                            value={operatingDays} onChange={(e) => setOperatingDays(Math.min(7, Math.max(1, Number(e.target.value) || 0)))}
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                    </Box>
                    <Box>
                        <Label>Operating Hours</Label>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField fullWidth size="small" type="number" inputProps={{ min: 0, max: 24, step: 1 }}
                                value={opStart} onChange={(e) => setOpStart(Math.min(24, Math.max(0, Number(e.target.value) || 0)))}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' }, endAdornment: <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP }}>:00</Typography> }} />
                            <Typography sx={{ color: COLORS.sub, fontFamily: PP }}>–</Typography>
                            <TextField fullWidth size="small" type="number" inputProps={{ min: 0, max: 24, step: 1 }}
                                value={opEnd} onChange={(e) => setOpEnd(Math.min(24, Math.max(0, Number(e.target.value) || 0)))}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' }, endAdornment: <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP }}>:00</Typography> }} />
                        </Box>
                    </Box>
                    {/* Daytime usage % — FOLLOWS operating hours by default; type to override,
                        clear to follow again. Always editable. (user 2026-06-23) */}
                    <Box>
                        <Label>Daytime usage (%) <span style={{ color: COLORS.sub, fontWeight: 400 }}>· used during solar hours</span></Label>
                        <TextField fullWidth size="small" type="number" inputProps={{ min: 0, max: 100, step: 5 }}
                            value={String(dayUsagePct).trim() !== '' ? dayUsagePct : String(_computedDayPct)} onChange={(e) => setDayUsagePct(e.target.value)}
                            onBlur={(e) => syncHoursFromDayPct(e.target.value)}
                            placeholder={String(_computedDayPct)}
                            InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' }, endAdornment: <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP }}>%</Typography> }} />
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 0.4 }}>
                            {String(dayUsagePct).trim() !== ''
                                ? `Manual ${_effDayPct}% — clear to follow operating hours (${_computedDayPct}%).`
                                : `Follows operating hours: ${_computedDayPct}% daytime (${Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8))}h in sun ÷ ${Math.max(0, opEnd - opStart)}h open). Type to override.`}
                        </Typography>
                    </Box>
                </Box>
                {/* Solar window explainer: how the export judgement works */}
                <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, mt: 1.25 }}>
                    Solar generates ~08:00–18:00. kWh produced DURING your operating hours is self-used; kWh produced OUTSIDE them (and on non-operating days) is exported. Operating days/week and hours drive how much is self-used vs exported.
                </Typography>
            </div>
            </Box>{/* /LEFT column */}

            {/* RIGHT column — system card (basis chooser inside) + outputs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', '& > *': { flex: '1 1 280px', minWidth: 0 } }}>
                <OptionCard
                    card={optADisplay}
                    label={`${tr(lang, 'Option A', '方案 A')}${basisA === 'p50' ? '' : ' · ' + basisLabel(basisA)}${_csMode === 'selco' ? ' (SELCo)' : ''}`}
                    lang={lang}
                    basis={basisA}
                    hasMd={isAtap}
                    mdKw={parseFloat(maxDemand) || 0}
                    selected={false}
                    gradient="linear-gradient(135deg,#1e1b4b,#312e81)"
                    sys={(() => {
                        const s = sysFor('a', optA);
                        // When MD is set and the user has NOT manually overridden inverterKw,
                        // reflect the MD-floored neededInv in the displayed inverter field.
                        if (_neededInv > 0 && (cscSys.a || {}).inverterKw == null) {
                            return { ...s, inverterKw: String(_neededInv) };
                        }
                        return s;
                    })()}
                    onSysChange={(f, v) => setSysField('a', f, v)}
                    battery={cscBattery.a}
                    onBatteryChange={(v) => setCardBatteryRaw('a', v)}
                    onBatteryBlur={(v) => setCardBattery('a', v)}
                    disc={discFor('a')}
                    onDiscChange={(patch) => setCardDisc('a', patch)}
                    om={sysFor('a', optA).om}
                    onOmChange={(v) => setCardOm('a', v)}
                    omPkg={cscOmPkg.a}
                    onOmPkgChange={(p) => setCardOmPkg('a', p)}
                    pricing={cardPricing('a', optA)}
                    addonRows={addonsForKey('a')}
                    catalog={addons}
                    onAddAddon={(id) => addAddonRow('a', id)}
                    onUpdateAddon={(rowId, patch) => updateAddonRow('a', rowId, patch)}
                    onRemoveAddon={(rowId) => removeAddonRow('a', rowId)}
                    mdWarn={_mdWarn}
                />
                <OptionCard
                    card={optBDisplay}
                    label={`${tr(lang, 'Option B', '方案 B')} · ${basisLabel(basisB)}${_csMode === 'selco' ? ' (SELCo)' : ''}`}
                    lang={lang}
                    basis={basisB}
                    hasMd={isAtap}
                    mdKw={parseFloat(maxDemand) || 0}
                    selected={false}
                    gradient="linear-gradient(135deg,#064e3b,#065f46)"
                    sys={sysFor('b', optB)}
                    onSysChange={(f, v) => setSysField('b', f, v)}
                    battery={cscBattery.b}
                    onBatteryChange={(v) => setCardBatteryRaw('b', v)}
                    onBatteryBlur={(v) => setCardBattery('b', v)}
                    disc={discFor('b')}
                    onDiscChange={(patch) => setCardDisc('b', patch)}
                    om={sysFor('b', optB).om}
                    onOmChange={(v) => setCardOm('b', v)}
                    omPkg={cscOmPkg.b}
                    onOmPkgChange={(p) => setCardOmPkg('b', p)}
                    pricing={cardPricing('b', optB)}
                    addonRows={addonsForKey('b')}
                    catalog={addons}
                    onAddAddon={(id) => addAddonRow('b', id)}
                    onUpdateAddon={(rowId, patch) => updateAddonRow('b', rowId, patch)}
                    onRemoveAddon={(rowId) => removeAddonRow('b', rowId)}
                    mdWarn={null}
                />
                </Box>
            </Box>{/* /RIGHT column */}
            </Box>{/* /left-right grid */}

            {/* Option chooser (BELOW the cards) — picks which package (A or B) the
                Section-2 Proposal + Section-3 Quotation/Forms/Create-Sales are generated
                for. RSC parity: choose the option, everything below follows it. Was
                hardcoded to 'a' (user 2026-06-22). */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {tr(lang, 'Generate for', '生成方案')}
                </Typography>
                {/* Simple RSC-style Opt buttons — contained when selected (user 2026-06-23). */}
                {['a', 'b'].map((key) => (
                    <Button key={key} size="small" variant={selectedOpt === key ? 'contained' : 'outlined'} onClick={() => setSelectedOpt(key)}
                        sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                            ...(selectedOpt === key
                                ? { bgcolor: '#0F766E', color: '#fff', '&:hover': { bgcolor: '#0c5e57', boxShadow: 'none' } }
                                : { borderColor: '#0F766E60', color: '#0F766E', '&:hover': { bgcolor: '#0F766E10', borderColor: '#0F766E' } }) }}>
                        {tr(lang, key === 'a' ? 'Option A' : 'Option B', key === 'a' ? '方案 A' : '方案 B')}
                    </Button>
                ))}
            </div>

            {/* Hourly solar-vs-usage explainer (one representative day · ×7 = a week) */}
            {optA && (parseFloat(bill) > 0) && (
                <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: COLORS.text, fontFamily: PP, mb: 0.25 }}>
                        {tr(lang, 'Daily Solar vs Usage', '每日用电模拟')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP, mb: 1.5 }}>
                        Toggle an operating vs non-operating day. How solar offsets the TNB bill hour by hour. {_fullSelf ? 'Assuming 100% self-use (no export).' : 'Off-load solar shown as export to grid.'}
                    </Typography>
                    <CscEnergyChart
                        kwp={optA.kwp}
                        monthlyKwh={(optA.selfConsumed || 0) + (optA.importAfterSolar || 0)}
                        opStart={opStart}
                        opEnd={opEnd}
                        fullSelfUse={_fullSelf}
                        operatingDays={operatingDays}
                    />
                </div>
            )}

            {/* WhatsApp Preview */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#075e54', p: '10px 16px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, bgcolor: '#128c7e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>💬</Box>
                    <div>
                        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', fontFamily: PP }}>{tr(lang, 'Commercial Solar Proposal', '商业太阳能方案')}</Typography>
                        <Typography sx={{ color: '#80cbc4', fontSize: '0.72rem', fontFamily: PP }}>Consolidated Proposal</Typography>
                    </div>
                </Box>
                <Box sx={{ bgcolor: '#e5ddd5', minHeight: 120, p: 1.5 }}>
                    <Box sx={{ bgcolor: '#fff', borderRadius: '0 8px 8px 8px', p: 1.25, maxWidth: '90%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <Typography component="pre" sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.72rem', lineHeight: 1.45, color: generatedMsg ? '#374151' : '#9CA3AF', fontStyle: generatedMsg ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                            {generatedMsg || tr(lang, 'Enter a bill to generate the WhatsApp message', '输入账单后自动生成 WhatsApp 信息')}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ p: '10px 16px', borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button onClick={onCopyMsg} disabled={!generatedMsg} variant="outlined" size="small"
                        sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
                        {copied ? tr(lang, 'Copied', '已复制') : tr(lang, 'Copy Message', '复制信息')}
                    </Button>
                    <Button onClick={onOpenWA} disabled={!generatedMsg} variant="contained" size="small"
                        sx={{ fontFamily: PP, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#25D366,#128C7E)', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg,#20b558,#0f7360)', boxShadow: 'none' } }}>
                        {tr(lang, 'Open WhatsApp', '开启 WhatsApp')}
                    </Button>
                </Box>
            </div>

            {/* ───────── Section 2 — Proposal ───────── */}
            <SectionHeader title="Section 2 — Proposal" />

            {/* Shared Project Info form */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>
                    Project Info
                </Typography>

                {/* Customer */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Customer
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Company Name" value={customerName} onChange={setCustomerName} required />
                    {/* Director / contact person — drives the quotation "Attn" + signature
                        person line (separate from the company). Same field as PIC > Name
                        below; fill either. (user 2026-06-23) */}
                    <Field label="Director / Contact Name" value={picName} onChange={setPicName} />
                    <Field label="Phone" value={customerPhone} onChange={setCustomerPhone} />
                    <Field label="Email" value={customerEmail} onChange={setCustomerEmail} />
                    <Field label="Address" value={customerAddress} onChange={setCustomerAddress} />
                    <Field label="Monthly Bill (RM)" value={bill} onChange={setBill} type="number" />
                </Box>

                {/* Site */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Site
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Site State (drives irradiance page)</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={siteState} onChange={(e) => setSiteState(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Not specified —</MenuItem>
                                {Object.entries(MY_STATE_GHI).map(([k, v]) => (
                                    <MenuItem key={k} value={k} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                        {v.name} <span style={{ color: COLORS.sub, marginLeft: 6 }}>· {v.ghi.toFixed(1)} kWh/m²/day</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Site Photos uploader (up to 4) */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Site Photos (optional, up to 4)</Typography>
                    <Button variant="outlined" component="label" size="small"
                        sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px' }}>
                        Add photo(s)
                        <input type="file" hidden accept="image/*" multiple
                            onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (!files.length) return;
                                const slotsLeft = Math.max(0, 4 - sitePhotos.length);
                                const toRead = files.slice(0, slotsLeft);
                                Promise.all(toRead.map((f) => new Promise((resolve) => {
                                    const fr = new FileReader();
                                    fr.onload = () => resolve(fr.result);
                                    fr.onerror = () => resolve(null);
                                    fr.readAsDataURL(f);
                                }))).then((urls) => {
                                    setSitePhotos((prev) => [...prev, ...urls.filter(Boolean)].slice(0, 4));
                                });
                                e.target.value = '';
                            }} />
                    </Button>
                    <Typography sx={{ fontSize: '0.68rem', color: COLORS.sub, fontFamily: PP, mt: 0.4, display: 'inline-block', ml: 1 }}>
                        {sitePhotos.length === 0 ? 'No photos — the Site Survey page will be omitted.' : `${sitePhotos.length} / 4 selected`}
                    </Typography>
                    {sitePhotos.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
                            {sitePhotos.map((src, i) => (
                                <Box key={i} sx={{ position: 'relative', width: 64, height: 64, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                                    <img src={src} alt={`Site ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <Button size="small" onClick={() => setSitePhotos((prev) => prev.filter((_, j) => j !== i))}
                                        sx={{ position: 'absolute', top: -4, right: -4, minWidth: 0, width: 20, height: 20, p: 0, fontSize: '0.7rem', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: '50%', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}>
                                        ×
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Financing */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Financing
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    <Field label="SARE Tariff (RM/kWh) — auto from kWp tier" value={sareTariffOverride} onChange={(v) => { setSareTariffOverride(v); setSareTouched(true); }} type="number" />
                    <Field label="Loan Principal (RM)" value={loanPrincipal} onChange={setLoanPrincipal} type="number" />
                    <Field label="Loan Rate (% p.a.)" value={loanRate} onChange={setLoanRate} type="number" />
                    <Field label="Loan Term (months)" value={loanTermMonths} onChange={setLoanTermMonths} type="number" />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeOutright} onChange={(e) => setIncludeOutright(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include Outright</Typography>}
                    />
                    {/* SARE only for ≥ 40 kWp — grey out + disable below that (user 2026-06-23). */}
                    <FormControlLabel
                        disabled={(parseFloat(sel?.kwp) || 0) < 40}
                        control={<Checkbox size="small" checked={includeSARE && (parseFloat(sel?.kwp) || 0) >= 40} onChange={(e) => setIncludeSARE(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: (parseFloat(sel?.kwp) || 0) >= 40 ? COLORS.text : COLORS.sub }}>Include SARE{(parseFloat(sel?.kwp) || 0) < 40 ? ' (≥40 kWp)' : ''}</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeLoan} onChange={(e) => setIncludeLoan(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include Loan</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeCA} onChange={(e) => setIncludeCA(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include CA</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeITA} onChange={(e) => { setItaTouched(true); setIncludeITA(e.target.checked); }} />}
                        label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP, color: COLORS.text }}>Include GITA</Typography>}
                    />
                </Box>

                {/* Prepared By */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mt: 2.5, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Prepared By
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Group</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={selectedGroupId} onChange={(e) => onPickGroup(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Select group —</MenuItem>
                                {agentGroups.map(g => (
                                    <MenuItem key={g.groupId} value={g.groupId} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{g.groupName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>Sales Agent Name</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={selectedAgentId} onChange={(e) => onPickAgent(e.target.value)} displayEmpty
                                sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.85rem', color: COLORS.sub }}>— Select agent —</MenuItem>
                                {groupAgents.map(a => (
                                    <MenuItem key={a.userId} value={a.userId} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                        {handleAgentName(a.firstName, a.lastName, a.alias)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Field label="Agent Position" value={sales.title} disabled />
                        <Typography sx={{ fontSize: '0.64rem', color: COLORS.sub, fontFamily: PP, mt: 0.3 }}>Auto from selected agent</Typography>
                    </Box>
                    <Field label="Email" value={sales.email} onChange={(v) => setSales(s => ({ ...s, email: v }))} />
                    <Field label="WhatsApp" value={sales.whatsapp} onChange={(v) => setSales(s => ({ ...s, whatsapp: v }))} />
                </Box>

                {/* Output actions — CSC Proposal (matches the Proposal tab: a
                    language toggle + Print Preview (vector) + Download PDF
                    (rasterized), both fed by the SAME buildProposalMap()). The
                    WhatsApp export lives in Section 1; the commercial forms are
                    in the group below. */}
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>
                        Generate Proposal
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button variant="outlined" disabled={!customerName.trim() || !sel.systemCost} onClick={printPreview}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', borderColor: COLORS.primary, color: COLORS.primary, '&:hover': { borderColor: COLORS.dark, backgroundColor: 'rgba(26,140,255,0.08)' } }}>
                            Print Preview (Ctrl+P → Save as PDF)
                        </Button>
                        <Button variant="contained" disabled={busy || !customerName.trim() || !sel.systemCost} onClick={genProposal}
                            sx={{ fontFamily: PP, fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { background: 'linear-gradient(135deg,#0F62CC,#0A4FA8)', boxShadow: 'none' } }}>
                            {busy ? 'Generating…' : 'Download PDF'}
                        </Button>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mt: 1 }}>
                        Print Preview = vector-quality (browser Save as PDF); Download PDF = rasterized (good for mobile).
                    </Typography>
                </Box>
                {err && <Typography sx={{ color: '#EF4444', fontSize: '0.74rem', fontFamily: PP, mt: 1 }}>{err}</Typography>}
            </div>

            {/* ───────── Section 3 — Quotation & Forms ───────── */}
            <SectionHeader title="Section 3 — Quotation & Forms" />

            {/* Company / Quotation Details — commercial form fields (feed ATAP Contract /
                Declaration / Insurance / Quotation). Moved here from the Proposal card. */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>
                    Company / Quotation Details
                </Typography>

                {/* Company Details */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Company Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Ref No" value={refNo} onChange={setRefNo} />
                    <Field label="Company TIN" value={companyTin} onChange={setCompanyTin} />
                    <Field label="Company Office (Tel)" value={companyOffice} onChange={setCompanyOffice} />
                    <Field label="Company Fax" value={companyFax} onChange={setCompanyFax} />
                    <Field label="Company Email" value={companyEmail} onChange={setCompanyEmail} />
                    <Field label="Company Website" value={companyWebsite} onChange={setCompanyWebsite} />
                </Box>

                {/* PIC (Person In Charge) */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    PIC (Person In Charge)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Salutation (Mr/Ms/Mdm/Dr)" value={picSalutation} onChange={setPicSalutation} />
                    <Field label="Name" value={picName} onChange={setPicName} />
                    <Field label="MyKad / IC" value={picMykad} onChange={setPicMykad} />
                    <Field label="Position" value={picPosition} onChange={setPicPosition} />
                    <Field label="Citizenship" value={picCitizenship} onChange={setPicCitizenship} />
                    <Field label="Mobile" value={picMobile} onChange={setPicMobile} />
                    <Field label="Email" value={picEmail} onChange={setPicEmail} />
                </Box>

                {/* Authorised / Alternate Person */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Authorised / Alternate Person
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Field label="Salutation (Mr/Ms/Mdm/Dr)" value={altSalutation} onChange={setAltSalutation} />
                    <Field label="Name" value={altName} onChange={setAltName} />
                    <Field label="MyKad / IC" value={altMykad} onChange={setAltMykad} />
                    <Field label="Position" value={altPosition} onChange={setAltPosition} />
                    <Field label="Relationship" value={altRelationship} onChange={setAltRelationship} />
                    <Field label="Citizenship" value={altCitizenship} onChange={setAltCitizenship} />
                    <Field label="Mobile" value={altMobile} onChange={setAltMobile} />
                    <Field label="Email" value={altEmail} onChange={setAltEmail} />
                </Box>

                {/* Warranty & Terms */}
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: COLORS.sub, fontFamily: PP, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Warranty &amp; Terms
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Field label="Operation Warranty (years)" value={operationWarranty} onChange={setOperationWarranty} />
                    <Field label="Defect Warranty" value={defectWarranty} onChange={setDefectWarranty} />
                    <Field label="Insurance Amount (RM)" value={insuranceAmount} onChange={setInsuranceAmount} type="number" />
                </Box>
            </div>

            {/* Payment Method — drives the Commercial Quotation (CC converts; loan/leasing note) */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 1 }}>
                    Payment Method
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    <ToggleButtonGroup exclusive size="small" value={payMethod} onChange={(_, v) => v && onPayMethod(v)}
                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.5, px: 1.6, color: COLORS.sub, borderColor: COLORS.border, borderRadius: '6px !important' }, '& .Mui-selected': { background: `${COLORS.primary} !important`, color: '#fff !important', borderColor: `${COLORS.primary} !important` } }}>
                        <ToggleButton value="cash">Cash</ToggleButton>
                        <ToggleButton value="cc">CC</ToggleButton>
                        <ToggleButton value="loan">Loan</ToggleButton>
                        <ToggleButton value="leasing">Leasing</ToggleButton>
                    </ToggleButtonGroup>
                    {payTermsFor(payMethod).length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select value={payTerm} onChange={(e) => setPayTerm(Number(e.target.value))}
                                sx={{ fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' }}>
                                {payTermsFor(payMethod).map(t => (
                                    <MenuItem key={t} value={t} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{t} months</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                {(() => {
                    const cash = (cardPricing(selectedOpt, selectedCard) || {}).cash || 0;
                    if (payMethod === 'cc') {
                        const conv = payAdjustedPrice(cash, 'cc', payTerm);
                        return (
                            <Typography sx={{ fontSize: '0.72rem', color: COLORS.text, fontFamily: PP, mt: 1 }}>
                                CC {payTerm}M price: <b>{fmtRM(conv)}</b> <span style={{ color: COLORS.sub }}>(cash {fmtRM(cash)} ÷ {CC_FACTORS[payTerm]}) — this converted amount drives the quotation's final columns.</span>
                            </Typography>
                        );
                    }
                    if (payMethod === 'loan' || payMethod === 'leasing') {
                        return (
                            <Typography sx={{ fontSize: '0.72rem', color: COLORS.warning, fontFamily: PP, mt: 1, fontStyle: 'italic' }}>
                                {payFinanceNote(payMethod)} (No price conversion — the quotation shows the cash price with this note.)
                            </Typography>
                        );
                    }
                    return (
                        <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mt: 1 }}>
                            Cash price — no conversion.
                        </Typography>
                    );
                })()}
            </div>

            {/* Create Sales — forms are downloaded from the sale page afterwards */}
            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                        Create Sales
                    </Typography>
                    <Button size="small" variant="outlined"
                        onClick={() => setClearDialogOpen(true)}
                        sx={{ fontFamily: PP, fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', flexShrink: 0, borderColor: '#CBD5E1', color: '#64748B', '&:hover': { borderColor: '#EF4444', color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' } }}>
                        Clear Data
                    </Button>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mb: 2 }}>
                    Create the sale to store the data — download the quotation and commercial forms from the sale page afterwards.
                </Typography>
                {(() => {
                    const disabledBase = !customerName.trim() || !sel.systemCost;
                    const anyRunning = quoteBusy || !!formBusy;
                    const canCreate = user?.roleRights?.includes('CreateSale');
                    if (!canCreate) {
                        return (
                            <Typography sx={{ fontSize: '0.74rem', color: COLORS.sub, fontFamily: PP, fontStyle: 'italic' }}>
                                You don't have permission to create sales.
                            </Typography>
                        );
                    }
                    return (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                            <Button size="small" variant="contained" disabled={disabledBase || anyRunning || submitBusy}
                                onClick={() => doSubmitSale(false)}
                                sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#16A34A,#15803D)', '&:hover': { background: 'linear-gradient(135deg,#15803D,#166534)', boxShadow: 'none' } }}>
                                {submitBusy ? 'Working…' : 'Create Sales'}
                            </Button>
                            <Button size="small" variant="outlined" disabled={disabledBase || anyRunning || submitBusy}
                                onClick={() => doSubmitSale(true)}
                                sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', borderColor: '#16A34A', color: '#16A34A', '&:hover': { borderColor: '#15803D', backgroundColor: 'rgba(22,163,74,0.08)' } }}>
                                {submitBusy ? 'Working…' : 'Create Sales & Another'}
                            </Button>
                        </Box>
                    );
                })()}
                {/* Quotation + commercial form PDF downloads — straight from the calculator. */}
                {(() => {
                    const disabledBase = !customerName.trim() || !sel.systemCost;
                    const anyRunning = quoteBusy || !!formBusy;
                    const primarySx = { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', '&:hover': { boxShadow: 'none', background: 'linear-gradient(135deg,#0F62CC,#0B4DA3)' }, '&.Mui-disabled': { background: '#E2E8F0', color: '#CBD5E1' } };
                    const outlinedSx = { fontFamily: PP, fontSize: '0.74rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', borderColor: '#CBD5E1', color: '#475569', '&:hover': { borderColor: '#1A8CFF', bgcolor: 'rgba(26,140,255,0.06)' }, '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#CBD5E1' } };
                    return (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 1, mt: 1 }}>
                                <Button size="small" variant="contained" disabled={disabledBase || anyRunning} onClick={genQuotation} sx={primarySx}>
                                    {quoteBusy ? 'Working…' : 'Quotation'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('atap', generateCommercialAtap)} sx={outlinedSx}>
                                    {formBusy === 'atap' ? 'Working…' : 'ATAP Contract'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('declaration', generateCommercialDeclaration)} sx={outlinedSx}>
                                    {formBusy === 'declaration' ? 'Working…' : 'Declaration'}
                                </Button>
                                <Button size="small" variant="outlined" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('insurance', generateCommercialInsurance2)} sx={outlinedSx}>
                                    {formBusy === 'insurance' ? 'Working…' : 'Insurance'}
                                </Button>
                                <Button size="small" variant="contained" disabled={disabledBase || anyRunning} onClick={() => runCommercialForm('all', generateCommercialAll)} sx={primarySx}>
                                    {formBusy === 'all' ? 'Working…' : 'All (ZIP)'}
                                </Button>
                            </Box>
                        </>
                    );
                })()}
                {err && <Typography sx={{ color: '#EF4444', fontSize: '0.74rem', fontFamily: PP, mt: 1.25 }}>{err}</Typography>}
            </div>
        </Box>
    );
}

function Field({ label, value, onChange, type = 'text', disabled, required }) {
    return (
        <Box>
            <Typography sx={{ fontSize: '0.74rem', color: '#374151', fontFamily: PP, mb: 0.4 }}>
                {label}{required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
            </Typography>
            <TextField fullWidth size="small" type={type} value={value} disabled={disabled}
                onChange={e => onChange && onChange(e.target.value)}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } }} />
        </Box>
    );
}
